import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { BarChart3, Download, FileText, Image, CheckCircle, Clock, AlertCircle, Loader, Code } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import * as XLSX from '../../utils/xlsxCompat';
import api from '../../api';
import { useProject } from '../../contexts/ProjectContext';
import {
  calculateCategoryCount,
  categorizeProcessStep,
  ExtendedProcessStep,
  DecisionPaths,
  BpmnType,
  TaskType,
  GatewayType,
  EventType,
  SequenceFlow,
  getBpmnElementColor,
  getBpmnTypeLabel,
  getGatewaySymbol,
  getEventSymbol,
  getTaskSymbol,
  GATEWAY_TYPE_LABELS,
  TASK_TYPE_LABELS,
  EVENT_TYPE_LABELS
} from '../../utils/processCategorization';
import { downloadBpmnFile } from '../../utils/bpmnExport';
import StepAuditPopup from './StepAuditPopup';
import ExceptionAnalysisPopup from './ExceptionAnalysisPopup';
import { useCanvasPan } from '../../hooks/useCanvasPan';
import ReorderableProcessList, { type FindingAction, type FindingActionOverrides } from './ReorderableProcessList';
import { buildEdges, edgeStyle, type MapEdge } from './buildEdges';
import { findingReason, overlayFindings, stakeholderFindings, stakeholderNamesFromMap, type AccuracyGateFinding } from '../../utils/accuracyGate';
import * as pdfjsLib from 'pdfjs-dist';

// PDF.js worker — use CDN to match installed version
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

interface StepAuditSummary {
  step_name: string;
  reasoning_chains: number;
  source_citations: number;
  cross_validations: number;
  has_audit_data: boolean;
}

interface AuditSummaryData {
  process_map_id: number;
  step_audit_summary: { [stepId: string]: StepAuditSummary };
  total_steps: number;
  steps_with_audit: number;
}

interface FrictionMetricsData {
  step_identifier: string;
  step_name: string;
  friction_score: number | null;
  friction_level: string | null;
  friction_color: string | null;
  error_rate?: number | null;
  manual_hours_weekly?: number | null;
  cycle_time_hours?: number | null;
  handoff_count?: number | null;
  exception_rate?: number | null;
  normalized_scores?: {
    error: number;
    effort: number;
    time: number;
    handoff: number;
    exception: number;
  } | null;
  data_source?: string;
  confidence_score?: number | null;
  has_data: boolean;
}

export interface ExportFunctions {
  downloadExcel: () => void;
  downloadPNG: () => void;
  downloadPDF: () => void;
  downloadBPMN: () => void;
  isDownloading: boolean;
}

interface SwimlanesVisualizationProps {
  swimlanes: any[];
  processes: any[];
  selectedLevel?: number;
  analysisResults?: any;
  onRefresh?: () => void;
  onStatisticsUpdate?: (stats: any) => void;
  showAuditInfo?: boolean;
  currentMap?: any;
  /**
   * @deprecated No longer rendered. The "Audit Info Ready" chip it drove was
   * removed when the audit trail became export-only. Kept on the interface so
   * existing call sites still typecheck; drop it once they are cleaned up.
   */
  auditEnhancementStatus?: {
    status: 'completed' | 'in_progress' | 'failed' | 'not_started' | 'deferred';
    progress: number;
    enhanced_steps_count: number;
    total_steps: number;
    error?: string;
  } | null;
  zoomLevel?: number;
  onZoomChange?: (zoom: number) => void;
  isEditMode?: boolean;
  onProcessReorder?: (reorderedProcesses: any[]) => void;
  onStepDelete?: (stepId: string) => void;
  onStepAdd?: (insertIndex: number, newStep: any) => void;
  onPendingDeleteChange?: (markedIds: string[]) => void;
  findings?: AccuracyGateFinding[];
  sourceHasDiamonds?: boolean;
  onFindingAction?: (finding: AccuracyGateFinding, action: FindingAction, overrides?: FindingActionOverrides) => void;
  onDecisionEdit?: (index: number, fields: { isDecision: boolean; decisionPaths?: FindingActionOverrides['decisionPaths'] }) => void;
  extraStakeholders?: string[];
  onExportReady?: (fns: ExportFunctions) => void;
  showFrictionHeatmap?: boolean;
  frictionDataMap?: Map<string, FrictionMetricsData>;
}


// ✅ HELPER: Normalize step reference to handle BOTH "Step 2" and "2" formats
// This ensures backward compatibility with existing process maps while supporting new Visio-compatible format
const normalizeStepReference = (stepRef: string): string => {
  if (!stepRef) return '';
  // Remove "Step " prefix if present (handles "Step 2" → "2", "Step 10A" → "10A")
  if (stepRef.startsWith('Step ')) {
    return stepRef.replace(/^Step\s+/, '').trim();
  }
  return stepRef.trim();
};

// ✅ HELPER: Find target step by step reference (handles both "Step 2" and "2" formats)
const findStepByReference = (stepRef: string, allSteps: any[]): any => {
  if (!stepRef || !allSteps || allSteps.length === 0) return null;

  const normalizedRef = normalizeStepReference(stepRef);

  // Try exact step_number match first (most reliable)
  let targetStep = allSteps.find(s => s.step_number === normalizedRef);
  if (targetStep) return targetStep;

  // Try matching step name patterns
  targetStep = allSteps.find(s =>
    s.name?.includes(`Step ${normalizedRef} -`) ||
    s.name?.includes(`Step ${normalizedRef}:`) ||
    s.step_number === normalizedRef
  );

  return targetStep;
};

// ✅ HELPER: Find processed step (with _TrackA/_TrackB suffix) by step reference
const findProcessedStepByReference = (stepRef: string, processedSteps: any[], trackSuffix?: string): any => {
  if (!stepRef || !processedSteps || processedSteps.length === 0) return null;

  const normalizedRef = normalizeStepReference(stepRef);

  return processedSteps.find(s => {
    // If track suffix specified, ensure step has that suffix
    if (trackSuffix && !s.id.includes(trackSuffix)) return false;

    // Try multiple matching strategies
    return (s as any).step_number === normalizedRef ||
      s.name?.includes(`Step ${normalizedRef}`) ||
      s.name?.includes(`${normalizedRef} -`) ||
      s.id.includes(normalizedRef);
  });
};

const enrichStepsWithAuditData = (steps: any[], auditData: any) => {
  if (!auditData) return steps;

  return steps.map(step => {
    const auditInfo = auditData.step_traceability?.find((trace: any) =>
      trace.final_step_name && step.name &&
      trace.final_step_name.toLowerCase().includes(step.name.toLowerCase().substring(0, 20))
    );

    if (auditInfo) {
      return {
        ...step,
        sources: auditInfo.source_documents || [],
        reasoning: auditInfo.reasoning || '',
        confidence_score: auditInfo.confidence_score
      };
    }

    return step;
  });
};


const extractStepTitle = (fullText: string): string => {
  if (!fullText) return '';

  const stepPattern = /^(Step\s+\d+[A-Z]?\s*-\s*[^:]+):/i;
  const match = stepPattern.exec(fullText);

  if (match) {
    return match[1].trim();
  }

  const stepNoColonPattern = /^(Step\s+\d+[A-Z]?\s*-\s*)/i;
  if (stepNoColonPattern.test(fullText)) {
    if (fullText.length > 50) {
      return fullText.substring(0, 47) + '...';
    }
    return fullText;
  }

  if (fullText.length > 50) {
    return fullText.substring(0, 47) + '...';
  }

  return fullText;
};

const getLevelName = (level: number) => {
  const names = {
    1: 'Strategic Overview',
    2: 'Tactical Processes',
    3: 'Operational Details',
    4: 'Execution Tasks'
  };
  return names[level as keyof typeof names] || 'Unknown Level';
};

interface TopScrollbarProps {
  scrollableRef: React.RefObject<HTMLDivElement>;
}

const TopScrollbar: React.FC<TopScrollbarProps> = ({ scrollableRef }) => {
  const [scrollRatio, setScrollRatio] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, scrollLeft: 0 });
  const [containerWidth, setContainerWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);

  const updateDimensions = useCallback(() => {
    if (!scrollableRef.current) return;
    const container = scrollableRef.current;
    const clientWidth = container.clientWidth;
    const scrollWidth = container.scrollWidth;
    setContainerWidth(clientWidth);
    setContentWidth(scrollWidth);
  }, [scrollableRef]);

  const thumbWidth = containerWidth > 0 && contentWidth > 0
    ? Math.max(20, (containerWidth / contentWidth) * containerWidth)
    : 20;
  const maxThumbLeft = Math.max(0, containerWidth - thumbWidth);
  const thumbLeft = scrollRatio * maxThumbLeft;
  const scrollPercentage = Math.round(scrollRatio * 100);

  useEffect(() => {
    const scrollableElement = scrollableRef.current;
    if (!scrollableElement) return;

    const handleScroll = () => {
      const scrollLeft = scrollableElement.scrollLeft;
      const maxScrollLeft = scrollableElement.scrollWidth - scrollableElement.clientWidth;
      const ratio = maxScrollLeft > 0 ? scrollLeft / maxScrollLeft : 0;
      setScrollRatio(ratio);
    };

    updateDimensions();
    handleScroll();

    scrollableElement.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', updateDimensions);

    const resizeObserver = new ResizeObserver(() => {
      updateDimensions();
      handleScroll();
    });
    resizeObserver.observe(scrollableElement);

    return () => {
      scrollableElement.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateDimensions);
      resizeObserver.disconnect();
    };
  }, [scrollableRef, updateDimensions]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollableRef.current) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX,
      scrollLeft: scrollableRef.current.scrollLeft
    });
    e.preventDefault();
    e.stopPropagation();
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !scrollableRef.current || !trackRef.current) return;
    const trackRect = trackRef.current.getBoundingClientRect();
    const deltaX = e.clientX - dragStart.x;
    const trackWidth = trackRect.width;
    const maxScrollLeft = scrollableRef.current.scrollWidth - scrollableRef.current.clientWidth;
    if (maxScrollLeft <= 0) return;
    const deltaRatio = deltaX / trackWidth;
    const newScrollLeft = Math.max(0, Math.min(maxScrollLeft, dragStart.scrollLeft + (deltaRatio * maxScrollLeft)));
    scrollableRef.current.scrollLeft = newScrollLeft;
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTrackClick = (e: React.MouseEvent) => {
    if (!scrollableRef.current || !trackRef.current || isDragging) return;
    const trackRect = trackRef.current.getBoundingClientRect();
    const clickX = e.clientX - trackRect.left;
    const clickRatio = Math.max(0, Math.min(1, clickX / trackRect.width));
    const maxScrollLeft = scrollableRef.current.scrollWidth - scrollableRef.current.clientWidth;
    if (maxScrollLeft <= 0) return;
    const newScrollLeft = clickRatio * maxScrollLeft;
    scrollableRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  if (contentWidth <= containerWidth || containerWidth === 0) {
    return null;
  }

  return (
    <div className="top-scrollbar-container">
      <div ref={trackRef} className="top-scrollbar-track" onClick={handleTrackClick}>
        <div
          className={`top-scrollbar-thumb ${isDragging ? 'dragging' : ''}`}
          style={{ width: `${thumbWidth}px`, left: `${thumbLeft}px` }}
          onMouseDown={handleMouseDown}
        />
        {scrollPercentage > 0 && (
          <div className="scrollbar-progress-indicator">{scrollPercentage}%</div>
        )}
      </div>
    </div>
  );
};

// ─── Preserve Mode PDF Viewer ────────────────────────────────────────────────
// Uses PDF.js canvas rendering (no browser plugin, no iframe) so Chrome's
// "This content is blocked" / object-src CSP restriction is bypassed entirely.

const PreservePDFViewer: React.FC<{ documentId: number | string; documentName?: string }> = ({
  documentId,
  documentName,
}) => {
  const [pages, setPages] = React.useState<string[]>([]);
  const [totalPages, setTotalPages] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [loadingPage, setLoadingPage] = React.useState(0);
  const [error, setError] = React.useState<string | null>(null);
  const [scale, setScale] = React.useState(1.4);
  const [downloadUrl, setDownloadUrl] = React.useState<string | null>(null);
  const pdfDocRef = React.useRef<any>(null);

  // Fetch PDF once per documentId
  React.useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    const fetchPDF = async () => {
      setLoading(true);
      setError(null);
      setPages([]);
      pdfDocRef.current = null;

      try {
        const res = await api.get(`/api/documents/view/${documentId}?preview=true`, {
          responseType: 'arraybuffer',
        });
        if (cancelled) return;

        const blob = new Blob([res.data], { type: 'application/pdf' });
        objectUrl = URL.createObjectURL(blob);
        setDownloadUrl(objectUrl);

        const pdf = await pdfjsLib.getDocument({ data: res.data }).promise;
        if (cancelled) return;
        pdfDocRef.current = pdf;
        setTotalPages(pdf.numPages);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Failed to load document');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPDF();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [documentId]);

  // Re-render pages when PDF doc is ready or scale changes
  React.useEffect(() => {
    const pdf = pdfDocRef.current;
    if (!pdf) return;

    let cancelled = false;

    const renderPages = async () => {
      const rendered: string[] = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        if (cancelled) return;
        setLoadingPage(i);
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d')!;
        await page.render({ canvasContext: ctx, viewport } as any).promise;
        rendered.push(canvas.toDataURL('image/png'));
      }
      if (!cancelled) {
        setPages(rendered);
        setLoadingPage(0);
      }
    };

    renderPages();
    return () => { cancelled = true; };
  }, [totalPages, scale]); // totalPages changes when doc loads → triggers initial render

  const isRendering = !loading && loadingPage > 0;

  if (loading || isRendering) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-electric-blue border-t-transparent" />
        <p className="text-gray-400 text-sm">
          {loadingPage > 0 ? `Rendering page ${loadingPage} of ${totalPages}…` : 'Loading document…'}
        </p>
      </div>
    );
  }

  if (error || (!loading && !isRendering && pages.length === 0)) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4 text-gray-400">
        <FileText className="w-12 h-12 opacity-40" />
        <p className="text-sm">{error || 'Unable to load document'}</p>
        <a
          href={`/api/documents/view/${documentId}?preview=true`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-electric-blue underline text-sm"
        >
          Open PDF in new tab
        </a>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full bg-gray-900" style={{ height: '80vh' }}>
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-4 py-2 bg-gray-800 border-b border-gray-700 text-sm text-gray-300 shrink-0">
        <FileText className="w-4 h-4 text-electric-blue shrink-0" />
        <span className="truncate flex-1">{documentName || 'Original Document'}</span>
        <span className="text-gray-500 shrink-0">{totalPages} page{totalPages !== 1 ? 's' : ''}</span>
        <button
          onClick={() => setScale(s => Math.max(0.5, s - 0.2))}
          className="px-2 py-0.5 rounded bg-gray-700 hover:bg-gray-600 shrink-0"
          title="Zoom out"
        >−</button>
        <span className="shrink-0">{Math.round(scale * 100)}%</span>
        <button
          onClick={() => setScale(s => Math.min(3, s + 0.2))}
          className="px-2 py-0.5 rounded bg-gray-700 hover:bg-gray-600 shrink-0"
          title="Zoom in"
        >+</button>
        {downloadUrl && (
          <a
            href={downloadUrl}
            download={documentName || 'document.pdf'}
            className="text-electric-blue hover:underline shrink-0"
          >
            Download
          </a>
        )}
      </div>
      {/* Pages */}
      <div className="flex-1 overflow-auto flex flex-col items-center gap-4 py-6 px-4">
        {pages.map((dataUrl, idx) => (
          <div key={idx} className="shadow-xl border border-gray-700">
            <img
              src={dataUrl}
              alt={`Page ${idx + 1}`}
              style={{ display: 'block', maxWidth: '100%' }}
              draggable={false}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

const SwimlanesVisualization: React.FC<SwimlanesVisualizationProps> = ({
  swimlanes,
  processes,
  selectedLevel = 3,
  analysisResults,
  onStatisticsUpdate,
  showAuditInfo = false,
  currentMap,
  auditEnhancementStatus,
  zoomLevel = 75,
  onZoomChange,
  isEditMode = false,
  onProcessReorder,
  onStepDelete,
  onStepAdd,
  onPendingDeleteChange,
  findings = [],
  sourceHasDiamonds = false,
  onFindingAction,
  onDecisionEdit,
  extraStakeholders = [],
  showFrictionHeatmap = false,
  frictionDataMap,
  onExportReady,
}) => {

  // 🔒 CHECK FOR PRESERVE MODE - Extract metadata for display banner
  const preserveMode = currentMap?.map_data?.metadata?.preserve_mode;
  const originalDocumentId = currentMap?.map_data?.metadata?.original_document_id;
  const originalDocumentName = currentMap?.map_data?.metadata?.original_document_name;
  const displayMode = currentMap?.map_data?.metadata?.display_mode;
  const originalDocumentPath = currentMap?.map_data?.metadata?.original_document_path;

  // 📄 CHECK FOR PDF IFRAME MODE - Display PDF instead of swimlanes
  if (displayMode === 'pdf_iframe' && originalDocumentId) {
    return (
      <PreservePDFViewer
        documentId={originalDocumentId}
        documentName={originalDocumentName}
      />
    );
  }

  // For preserve mode, we'll show a banner but continue with normal swim lane rendering
  // The extracted data from backend will be shown in the normal UI

  // Check for preserve mode - if it's preserve mode, don't require action_type
  const isPreserveMode = preserveMode && originalDocumentId;

  // Check if we have complete data (not just count, but actual action_type data)
  // For preserve mode, we only need processes to exist (no action_type required)
  const hasCompleteProcessData = isPreserveMode
    ? (processes && processes.length > 0)  // Preserve mode: just check if processes exist
    : (processes && processes.length > 0 && processes.some(p => p.action_type !== undefined));  // Normal mode: check for action_type

  if (!hasCompleteProcessData && !isPreserveMode) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-600">Loading process data...</span>
      </div>
    );
  }

  // Keep track of processed decision steps across re-renders to prevent duplicates
  const processedDecisionStepsRef = useRef(new Set<string>());

  const hasRealProcessData = processes && processes.length > 0;

  const assignProcessToStakeholder = (processText: string, allProcesses: any[]) => {
    // Use process map swimlanes data instead of synthesis fallback
    const availableRoles = swimlanes && swimlanes.length > 0
      ? swimlanes.map(lane => lane.name)
      : allRoles;

    if (availableRoles.length === 0) {
      return 'Process Owner';
    }

    const textLower = processText.toLowerCase();

    // Try to match role names in the process text
    for (const role of availableRoles) {
      const roleKeywords = role.toLowerCase().split(/[\s,&]+/);
      const hasRoleMatch = roleKeywords.some((keyword: string) =>
        keyword.length > 2 && textLower.includes(keyword)
      );

      if (hasRoleMatch) {
        return role;
      }
    }

    // Default to first available role
    return availableRoles[0];
  };


  if (!hasRealProcessData) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <div className="max-w-md mx-auto">
          <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">No Process Map Data</h3>
          <p className="text-gray-500 mb-4">
            Generate a process map first to see the swimlane visualization.
          </p>
        </div>
      </div>
    );
  }

  const abortControllerRef = useRef<AbortController | null>(null);
  const swimlanesRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(true);

  const [isDownloading, setIsDownloading] = React.useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const { currentProject } = useProject();

  // Figma-style canvas panning
  const { isPanning, isPanMode, canvasProps } = useCanvasPan({
    scrollableRef: swimlanesRef,
    zoomLevel,
    isEditMode,
  });

  const [auditPopupOpen, setAuditPopupOpen] = useState(false);
  const [selectedStepForAudit, setSelectedStepForAudit] = useState<{
    stepId: string;
    stepName: string;
    full_text: string;
    processMapId: number;
    processId?: string;
    stepDbId?: number;
    validationStatus?: 'ai_generated' | 'human_verified' | 'human_modified';
    isLocked?: boolean;
  } | null>(null);
  const [loadingStepDetails, setLoadingStepDetails] = useState(false);
  const [auditSummary, setAuditSummary] = useState<AuditSummaryData | null>(null);

  const [stepExceptionData, setStepExceptionData] = useState<Map<string, any>>(new Map());

  const [exceptionPopupOpen, setExceptionPopupOpen] = useState(false);
  const [selectedStepException, setSelectedStepException] = useState<any>(null);


  const renderStepTooltip = useCallback((step: ExtendedProcessStep, showAudit: boolean = false) => {
    let tooltip = `${step.name}`;

    // Add Original vs AI Added indicator
    const sourceCitations = (step as any).source_citations;
    if (step.name !== 'Start' && step.name !== 'End') {
      if (sourceCitations && sourceCitations.length > 0) {
        tooltip += `\n\n✓ Original: From your document`;
        tooltip += `\n• Source: ${sourceCitations[0]}`;
      } else {
        tooltip += `\n\n+ AI Added: Inferred by AI`;
      }
    }

    // Add assignee/stakeholder information
    if (step.assignee || step.swimlane || (step as any).actor) {
      tooltip += `\n\n👤 Stakeholder Information:`;

      const assignee = step.assignee || (step as any).actor;
      const swimlane = step.swimlane;
      const realName = (step as any).realName;
      const stakeholderRationale = (step as any).stakeholderRationale;

      if (assignee) {
        tooltip += `\n• Assigned to: ${assignee}`;
      }
      if (swimlane && swimlane !== assignee) {
        tooltip += `\n• Swimlane: ${swimlane}`;
      }
      if (realName) {
        tooltip += `\n• Real Name: ${realName}`;
      }
      if (stakeholderRationale) {
        tooltip += `\n• Assignment Rationale: ${stakeholderRationale}`;
      }
    }

    if ((step.type === 'decision' || step.type === 'split') && (step.decision_paths || step.decisionPaths)) {
      const paths = step.decision_paths || step.decisionPaths;
      tooltip += `\n\n🔀 Decision Paths:`;
      if (paths?.yes) {
        tooltip += `\n✅ Yes → ${paths.yes}`;
        if (paths.yes_condition) {
          tooltip += ` (${paths.yes_condition})`;
        }
      }
      if (paths?.no) {
        tooltip += `\n❌ No → ${paths.no}`;
        if (paths.no_condition) {
          tooltip += ` (${paths.no_condition})`;
        }
      }
    }

    if (step.action_type) {
      tooltip += `\n\n🤖 AI Analysis:`;
      tooltip += `\n• Action Type: ${step.action_type}`;
      const bpmnType = (step as any).bpmn_type;
      const taskType = (step as any).task_type;
      const gatewayType = (step as any).gateway_type;
      const eventType = (step as any).event_type;
      if (bpmnType) {
        tooltip += `\n• BPMN Type: ${bpmnType}`;
        if (bpmnType === 'task' && taskType) {
          tooltip += ` (${TASK_TYPE_LABELS[taskType] || taskType})`;
        }
        if (bpmnType === 'gateway' && gatewayType) {
          tooltip += ` (${GATEWAY_TYPE_LABELS[gatewayType] || gatewayType})`;
        }
        if (bpmnType === 'event' && eventType) {
          tooltip += ` (${EVENT_TYPE_LABELS[eventType] || eventType})`;
        }
      }
      if (step.decision_criteria) {
        tooltip += `\n• Decision Criteria: ${step.decision_criteria}`;
      }
      if (step.confidence_score) {
        tooltip += `\n• Confidence: ${step.confidence_score}%`;
      }
      const outgoingFlows = (step as any).outgoing_flows;
      const incomingFlows = (step as any).incoming_flows;
      if (outgoingFlows && outgoingFlows.length > 0) {
        tooltip += `\n• Outgoing Flows: ${outgoingFlows.length}`;
      }
      if (incomingFlows && incomingFlows.length > 0) {
        tooltip += `\n• Incoming Flows: ${incomingFlows.length}`;
      }
    }

    const exceptionInfo = stepExceptionData.get(step.id);

    // Exception detail stays in the tooltip only when there is something actionable
    // to say. The "Complete / no exceptions found" and "processing in background"
    // variants were removed: they told the reviewer nothing they could act on and
    // read as an unearned green light. Full exception detail now ships in the
    // exported audit trail (Export → Audit Trail).
    if (exceptionInfo?.hasExceptions) {
      tooltip += `\n\n🔄 Exception Analysis:`;
      tooltip += `\n• ${exceptionInfo.totalExceptions} exception types identified`;

      if (exceptionInfo.totalExceptionProcesses > 0) {
        tooltip += `\n• ${exceptionInfo.totalExceptionProcesses} dedicated exception processes`;
      }

      tooltip += `\n• Risk Level: ${exceptionInfo.riskLevel.toUpperCase()}`;

      const exceptionPaths = exceptionInfo.stepData?.exception_paths || [];
      if (exceptionPaths.length > 0) {
        tooltip += `\n🚨 Exception Paths: ${exceptionPaths.length} alternative flows`;
        exceptionPaths.slice(0, 2).forEach((path: any, index: number) => {
          tooltip += `\n  ${index + 1}. ${path.trigger}`;
        });
        if (exceptionPaths.length > 2) {
          tooltip += `\n  ...and ${exceptionPaths.length - 2} more`;
        }
      }

      tooltip += `\n\n💡 Click the orange indicator (!) for detailed exception analysis`;
    }

    return tooltip;
  }, [stepExceptionData]);



  useEffect(() => {
    abortControllerRef.current = new AbortController();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);



  const loadAuditSummary = useCallback(async () => {
    if (!currentMap?.id) return;

    try {
      const token = localStorage.getItem('jarvis_access_token');
      const response = await api.get(
        `/api/analysis/process-maps/${currentMap.id}/audit-summary`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setAuditSummary(response.data);
    } catch (error) {
    } finally {

    }
  }, [currentMap?.id]);


  useEffect(() => {
    if (currentMap?.id && showAuditInfo) {
      loadAuditSummary();
    }
  }, [currentMap?.id, showAuditInfo, loadAuditSummary]);


  const handleStepAuditClick = async (stepId: string, stepName: string, originalText: string) => {
    if (!currentMap?.id) return;

    setLoadingStepDetails(true);

    try {
      // Extract step number from stepId (e.g., "L3_step_1.2.3" -> "1.2.3")
      const stepNumber = stepId.split('_').pop() || stepId;

      // Fetch step details from backend
      const token = localStorage.getItem('jarvis_access_token') || localStorage.getItem('authToken');
      const response = await api.get(
        `/api/analysis/process-map-steps/${currentMap.id}/step/${stepNumber}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success && response.data.step) {
        const stepData = response.data.step;

        setSelectedStepForAudit({
          stepId,
          stepName: originalText || stepName,
          processMapId: currentMap.id,
          full_text: originalText,
          processId: currentMap.process_id,
          stepDbId: stepData.id,
          validationStatus: stepData.validation_status,
          isLocked: stepData.locked
        });

        setAuditPopupOpen(true);
      } else {
        // Fallback if step details not found
        setSelectedStepForAudit({
          stepId,
          stepName: originalText || stepName,
          processMapId: currentMap.id,
          full_text: originalText,
          processId: currentMap.process_id
        });
        setAuditPopupOpen(true);
      }
    } catch (error: any) {
      // Silently handle 404 - step details not synced to process_map_steps table yet (expected)
      if (error?.response?.status !== 404) {
        console.error('Failed to fetch step details:', error);
      }
      // Open popup anyway without metadata
      setSelectedStepForAudit({
        stepId,
        stepName: originalText || stepName,
        processMapId: currentMap.id,
        full_text: originalText,
        processId: currentMap.process_id
      });
      setAuditPopupOpen(true);
    } finally {
      setLoadingStepDetails(false);
    }
  };


  const convertProcessesToSwimlanes = React.useCallback(() => {
    if (!processes || processes.length === 0) {
      return { roles: ['Process Owner'], processSteps: [], stakeholderCounts: {} };
    }

    const exactStakeholders = new Set<string>();
    const exactRoles = new Set<string>();
    const businessAreas = new Set<string>();

    // Backend swimlanes are the authoritative list — they reflect every
    // approved stakeholder. Seed the role list from them first, in order.
    if (swimlanes && swimlanes.length > 0) {
      swimlanes.forEach(lane => {
        if (lane?.name && lane.name.trim()) {
          exactStakeholders.add(lane.name);
        }
      });
    }

    // Then add any additional actors/assignees from process steps that
    // are not already represented as a swimlane (defensive fallback).
    processes.forEach(process => {
      const candidate = (process.actor && process.actor.trim())
        || (process.assignee && process.assignee.trim())
        || '';
      if (candidate && candidate !== 'Unassigned' && candidate !== 'Process Owner') {
        exactStakeholders.add(candidate);
      }
      if (process.real_name) {
        exactStakeholders.add(`${process.assignee} (${process.real_name})`);
      }
    });

    const roles: string[] = Array.from(exactStakeholders);

    if (roles.length === 0) {
      roles.push('Process Owner');
      if (selectedLevel >= 2) roles.push('Stakeholder');
      if (selectedLevel >= 3) roles.push('System Layer');
    }

    const processSteps = processes.map((process, index) => {
      let assignedLane = process.actor && roles.includes(process.actor)
        ? process.actor
        : null;

      if (!assignedLane) {
        assignedLane = process.assignee && roles.includes(process.assignee)
          ? process.assignee
          : roles[0];
      }

      return {
        ...process,
        swimlane: assignedLane,
        assignee: process.actor || process.assignee || assignedLane,
        realName: process.real_name || null,
        stakeholderRationale: process.stakeholder_rationale || null
      };
    });

    return {
      roles,
      processSteps: processSteps as ExtendedProcessStep[],
      stakeholderCounts: {
        exactStakeholders: exactStakeholders.size,
        exactRoles: exactRoles.size,
        businessAreas: businessAreas.size,
        systemsAndPlatforms: 0
      }
    };
  }, [processes, swimlanes, selectedLevel, analysisResults?.process_synthesis?.updated_at]);

  const { roles: allRoles, processSteps, stakeholderCounts } = React.useMemo(() => {
    return convertProcessesToSwimlanes();
  }, [convertProcessesToSwimlanes]);

  const roles = React.useMemo(() => {
    if (allRoles.length === 0) {
      return ['Process Owner'];
    }
    return allRoles;
  }, [allRoles]);

  const generateDecisionPaths = (step: any, index: number, allSteps: any[]): DecisionPaths | undefined => {
    // Handle parallel tracks from database

    if (step.decision_paths) {
      // Handle parallel tracks (Track A & Track B)
      if (step.decision_paths.parallel_tracks) {
        const tracks = step.decision_paths.parallel_tracks;

        // Convert step references to proper IDs for parallel tracks
        let trackAFirst = tracks.track_A?.next_steps?.[0] || '';
        let trackBFirst = tracks.track_B?.next_steps?.[0] || '';
        let convergenceStep = tracks.track_A?.convergence || tracks.track_B?.convergence || '';

        // Convert step references to step IDs (handles both "Step 2" and "2" formats)
        if (trackAFirst) {
          const targetStep = findStepByReference(trackAFirst, allSteps);
          if (targetStep) {
            trackAFirst = targetStep.id;
          }
        }

        if (trackBFirst) {
          const targetStep = findStepByReference(trackBFirst, allSteps);
          if (targetStep) {
            trackBFirst = targetStep.id;
          }
        }

        if (convergenceStep) {
          const targetStep = findStepByReference(convergenceStep, allSteps);
          if (targetStep) {
            convergenceStep = targetStep.id;
          }
        }

        return {
          yes: trackAFirst, // First step of Track A
          no: trackBFirst,  // First step of Track B
          yes_condition: tracks.track_A?.name || 'Track A',
          no_condition: tracks.track_B?.name || 'Track B',
          parallel_tracks: {
            ...tracks,
            track_A: {
              ...(tracks.track_A || {}),
              convergence: convergenceStep
            },
            track_B: {
              ...(tracks.track_B || {}),
              convergence: convergenceStep
            }
          }
        };
      }

      // Handle simple yes/no paths or completion paths
      if (step.decision_paths.yes || step.decision_paths.no || step.decision_paths.completion) {
        let yesPath = step.decision_paths.yes;
        let noPath = step.decision_paths.no;
        let completionPath = step.decision_paths.completion;

        const hasABSuffix = (ref: string) => ref && /[A-B]$/i.test(ref);
        const yesHasTrack = hasABSuffix(yesPath);
        const noHasTrack = hasABSuffix(noPath);

        if (yesHasTrack || noHasTrack) {
          const findTrackSteps = (startRef: string, trackLetter: string) => {
            const steps: string[] = [];
            if (!startRef) return steps;
            const baseNum = startRef.replace(/[A-B]$/i, '');
            for (const s of allSteps) {
              const stepNum = s.step_number || '';
              if (stepNum.endsWith(trackLetter) && parseInt(stepNum.replace(/[A-B]$/i, '')) >= parseInt(baseNum)) {
                steps.push(stepNum);
              }
            }
            return steps.sort((a, b) => {
              const numA = parseInt(a.replace(/[A-B]$/i, ''));
              const numB = parseInt(b.replace(/[A-B]$/i, ''));
              return numA - numB;
            });
          };

          const findConvergence = (trackSteps: string[]) => {
            if (trackSteps.length === 0) return '';
            const lastTrackStep = trackSteps[trackSteps.length - 1];
            const lastNum = parseInt(lastTrackStep.replace(/[A-B]$/i, ''));
            for (const s of allSteps) {
              const stepNum = s.step_number || '';
              const stepNumInt = parseInt(stepNum);
              if (!isNaN(stepNumInt) && stepNumInt > lastNum && !/[A-B]$/i.test(stepNum)) {
                return stepNum;
              }
            }
            return '';
          };

          const trackALetter = yesPath?.slice(-1).toUpperCase() || 'A';
          const trackBLetter = noPath?.slice(-1).toUpperCase() || 'B';
          const trackASteps = findTrackSteps(yesPath, trackALetter);
          const trackBSteps = findTrackSteps(noPath, trackBLetter);
          const convergenceA = findConvergence(trackASteps);
          const convergenceB = findConvergence(trackBSteps);
          const convergence = convergenceA || convergenceB;

          const trackAFirstStep = findStepByReference(trackASteps[0] || yesPath, allSteps);
          const trackBFirstStep = findStepByReference(trackBSteps[0] || noPath, allSteps);
          const convergenceStep = convergence ? findStepByReference(convergence, allSteps) : null;

          return {
            yes: trackAFirstStep?.id || yesPath,
            no: trackBFirstStep?.id || noPath,
            yes_condition: step.decision_paths.yes_condition || 'Track A',
            no_condition: step.decision_paths.no_condition || 'Track B',
            parallel_tracks: {
              track_A: {
                name: step.decision_paths.yes_condition || 'If Yes',
                next_steps: trackASteps,
                convergence: convergenceStep?.step_number || convergence
              },
              track_B: {
                name: step.decision_paths.no_condition || 'If No',
                next_steps: trackBSteps,
                convergence: convergenceStep?.step_number || convergence
              }
            }
          };
        }

        if (yesPath) {
          const targetStep = findStepByReference(yesPath, allSteps);
          if (targetStep) {
            yesPath = targetStep.id;
          }
        }

        if (noPath) {
          const targetStep = findStepByReference(noPath, allSteps);
          if (targetStep) {
            noPath = targetStep.id;
          }
        }

        if (completionPath) {
          const targetStep = findStepByReference(completionPath, allSteps);
          if (targetStep) {
            completionPath = targetStep.id;
          }
        }

        const result: DecisionPaths = {
          yes: yesPath || completionPath,
          no: noPath,
          yes_condition: step.decision_paths.yes_condition || 'Yes',
          no_condition: step.decision_paths.no_condition || 'No'
        };

        if (step.decision_paths.no_path_details) {
          (result as any).no_path_details = step.decision_paths.no_path_details;
        }

        return result;
      }
    }

    // Legacy support
    if (step.decisionPaths && (step.decisionPaths.yes || step.decisionPaths.no)) {
      return {
        yes: step.decisionPaths.yes,
        no: step.decisionPaths.no
      };
    }

    return undefined;
  };


  const generateLeveledProcesses = () => {
    const baseSteps = processes || [];

    if (!baseSteps || baseSteps.length === 0) {
      return [];
    }

    const hasRealData = baseSteps.some(step =>
      step.sync_source === 'process_synthesis' ||
      step.sources?.length > 0 ||
      step.id?.includes('synthesis_step') ||
      step.name?.length > 10 ||
      step.step_index !== undefined
    );

    if (!hasRealData) {
      return [];
    }

    const categorizeStep = (step: any, index: number, totalSteps: number) => {
      const result = categorizeProcessStep(step, index, totalSteps);
      return { type: result.type, category: result.category };
    };

    switch (selectedLevel) {
      case 1:
        // Use ALL steps from process synthesis (1:1 mapping, no consolidation)
        return baseSteps.map((step, index) => {
          // Force strategic level steps to be 'process' type (no decisions for Level 1)
          const { category } = categorizeStep(step, index, baseSteps.length);
          const type = 'process'; // Always process type for strategic level
          const aiAssignedRole = step.actor || step.assignee;
          const assignedRole = aiAssignedRole && allRoles.includes(aiAssignedRole)
            ? aiAssignedRole
            : assignProcessToStakeholder(step.name || step.step || '', processes);
          const laneIndex = allRoles.indexOf(assignedRole ?? '');

          return {
            id: `L1_${step.id || index}`,
            name: step.name || step.step || 'Step',
            full_text: step.stepText,
            description: step.description || step.inference || '',
            type: type,
            category: category,
            action_type: 'process', // Force process type for strategic level
            assignee: assignedRole,
            swimlane: assignedRole,
            position: { x: index, y: Math.max(0, laneIndex) },
            connections: index < baseSteps.length - 1 ? [`L1_${baseSteps[index + 1]?.id || (index + 1)}`] : [],
            sources: step.sources || [],
            globalPosition: index
          };
        });

      case 2:
        return baseSteps.map((step, index) => {
          const { type, category } = categorizeStep(step, index, baseSteps.length);
          const stepText = step.name || step.step || '';
          const aiAssignedRole = step.actor || step.assignee;
          const assignedRole = aiAssignedRole && allRoles.includes(aiAssignedRole)
            ? aiAssignedRole
            : assignProcessToStakeholder(step.name || step.step || '', processes);
          const laneIndex = allRoles.indexOf(assignedRole ?? '');

          const decisionPaths = (type === 'decision' || type === 'split') ? generateDecisionPaths({
            ...step,
            type,
            name: stepText,
            id: step.id || index
          }, index, baseSteps) : undefined;

          return {
            ...step,
            id: `L2_${step.id || index}`,
            name: stepText,
            type: type,
            category: category,
            action_type: step.action_type || 'process',
            assignee: assignedRole,
            swimlane: assignedRole,
            position: { x: index, y: Math.max(0, laneIndex) },
            connections: index < baseSteps.length - 1 ? [`L2_${baseSteps[index + 1]?.id || (index + 1)}`] : [],
            decisionPaths: decisionPaths,
            sources: step.sources || [],
            full_text: step.stepText || '',
            description: step.description || step.inference || '',
            globalPosition: index,
            reasoning: step.reasoning || '',
            confidence_score: step.confidence_score || 75,
            extraction_type: step.extraction_type || 'synthesis',
            document_id: step.document_id,
            page_reference: step.page_reference,
            isTrackStep: false,
            bpmn_type: step.bpmn_type,
            gateway_type: step.gateway_type,
            task_type: step.task_type,
            event_type: step.event_type
          };
        });

      case 3: {
        const allSteps: any[] = [];
        const trackStepsCreated = new Set<string>();

        baseSteps.forEach((step, index) => {

          // Only skip steps that are DIRECT children of parallel tracks that we're currently processing
          // This prevents over-skipping legitimate steps
          // NEVER skip steps that have decision_paths (they are decision steps, not track children)
          const hasAnyDecisionPaths = step.decision_paths && (step.decision_paths.yes || step.decision_paths.no || step.decision_paths.parallel_tracks);
          const shouldSkipStep = !hasAnyDecisionPaths && (trackStepsCreated.has(`${step.step_number}_A`) || trackStepsCreated.has(`${step.step_number}_B`));


          if (shouldSkipStep) {
            return; // Skip this step
          }


          const { type, category } = categorizeStep(step, index, baseSteps.length);
          const stepText = step.name || step.step || step.text || '';
          const aiAssignedRole = step.actor || step.assignee;
          const assignedRole = aiAssignedRole && allRoles.includes(aiAssignedRole)
            ? aiAssignedRole
            : assignProcessToStakeholder(step.name || step.step || '', processes);
          const laneIndex = allRoles.indexOf(assignedRole ?? '');

          const decisionPaths = (type === 'decision' || type === 'split') ? generateDecisionPaths({
            ...step,
            type,
            name: stepText,
            id: step.id || index,
            step: stepText
          }, index, baseSteps) : undefined;

          // Handle completion path connections (handles both "Step 2" and "2" formats)
          const connections = [] as string[];
          if (step.decision_paths?.completion) {
            const completionPath = step.decision_paths.completion;
            const targetStep = findStepByReference(completionPath, baseSteps);
            if (targetStep) {
              const targetId = targetStep.id;
              connections.push(targetId);
            }
          }

          const mainStep = {
            ...step,
            id: step.id || `step_${index}`,
            name: stepText,
            type: type,
            category: category,
            action_type: step.action_type || 'process',
            assignee: assignedRole,
            swimlane: assignedRole,
            position: { x: index, y: Math.max(0, laneIndex) },
            connections: connections,
            decisionPaths: decisionPaths,
            decision_paths: step.decision_paths,
            sources: step.sources || [],
            reasoning: step.reasoning || '',
            confidence_score: step.confidence_score || 75,
            full_text: step.stepText || '',
            description: step.description || step.inference || '',
            globalPosition: index,
            extraction_type: step.extraction_type || 'synthesis',
            document_id: step.document_id,
            page_reference: step.page_reference,
            isTrackStep: false,
            bpmn_type: step.bpmn_type,
            gateway_type: step.gateway_type,
            task_type: step.task_type,
            event_type: step.event_type
          };

          allSteps.push(mainStep);

          // Handle decision/split step with parallel tracks - create isolated track flows
          // Use decisionPaths which may have been converted from yes/no with A/B suffixes
          const hasParallelTracks = decisionPaths?.parallel_tracks || step.decision_paths?.parallel_tracks;
          if ((type === 'decision' || type === 'split') && hasParallelTracks) {
            const tracks = decisionPaths?.parallel_tracks || step.decision_paths?.parallel_tracks;

            // Get assignees for dynamic lane separation (handles both "Step 2" and "2" formats)
            const getFirstTrackStepAssignee = (trackSteps: string[]) => {
              if (!trackSteps || trackSteps.length === 0) return null;
              const firstStepId = trackSteps[0];

              // Use helper function to find step (handles both formats)
              const originalStep = findStepByReference(firstStepId, baseSteps);

              return originalStep?.assignee || originalStep?.actor || null;
            };

            const trackAAssignee = getFirstTrackStepAssignee(tracks.track_A?.next_steps || []);
            const trackBAssignee = getFirstTrackStepAssignee(tracks.track_B?.next_steps || []);
            const hasSameAssignee = trackAAssignee && trackBAssignee && trackAAssignee === trackBAssignee;

            const decisionLaneIndex = laneIndex >= 0 ? laneIndex : 0;
            const decisionStepAssignee = assignedRole;

            if (tracks.track_A?.next_steps) {
              const firstTrackAStepId = tracks.track_A.next_steps[0];
              const firstTrackAStep = findStepByReference(firstTrackAStepId, baseSteps);
              const trackAAssigneeForAll = decisionStepAssignee;
              const trackAFinalLane = decisionLaneIndex;

              tracks.track_A.next_steps.forEach((trackStepId: string, trackIndex: number) => {
                const originalStep = findStepByReference(trackStepId, baseSteps);

                // Skip creating track versions of steps that have their own decision_paths
                // These should remain as original decision steps, not track steps
                const isDecisionStep = originalStep?.decision_paths && (originalStep.decision_paths.yes || originalStep.decision_paths.no);

                if (originalStep && !trackStepsCreated.has(`${trackStepId}_A`) && !isDecisionStep) {
                  // Use TRACK A assignee for ALL steps in Track A (not individual step assignee)
                  const trackAssignee = trackAAssigneeForAll;
                  const finalLaneIndex = trackAFinalLane;

                  const trackStep = {
                    ...originalStep,  // ✅ Preserve ALL fields from database (including Visio fields)
                    id: `${originalStep.id}_TrackA`,
                    name: originalStep.name || `${tracks.track_A.name} - Step ${trackStepId}`,
                    type: 'process', // Force track steps to be process type
                    category: 'process',
                    action_type: 'process', // Force action_type to process to prevent decision processing
                    decision_paths: undefined, // Remove decision paths to prevent processing as decision
                    assignee: trackAssignee,
                    swimlane: trackAssignee,
                    position: {
                      x: index + trackIndex + 1,
                      y: trackAFinalLane
                    },
                    connections: (() => {
                      if (trackIndex < tracks.track_A.next_steps.length - 1) {
                        // Connect to next step in track
                        const nextTrackStep = baseSteps.find(s =>
                          s.step_number === tracks.track_A.next_steps[trackIndex + 1] ||
                          s.name?.includes(`Step ${tracks.track_A.next_steps[trackIndex + 1]} -`) ||
                          s.name?.includes(`Step ${tracks.track_A.next_steps[trackIndex + 1]}:`)
                        );
                        return nextTrackStep ? [`${nextTrackStep.id}_TrackA`] : [];
                      } else {
                        // Last step in track - connect to convergence point if specified (handles both "Step 2" and "2" formats)
                        const convergenceRef = tracks.track_A.convergence || tracks.convergence;
                        if (convergenceRef) {
                          const convergenceStep = findStepByReference(convergenceRef, baseSteps);
                          if (convergenceStep) {
                            const convergenceId = convergenceStep.id;
                            return [convergenceId];
                          }
                        }
                        // No further connections - track ends here
                        return [] as string[];
                      }
                    })(),
                    sources: originalStep.sources || [],
                    reasoning: originalStep.reasoning || '',
                    confidence_score: originalStep.confidence_score || 75,
                    full_text: originalStep.stepText || '',
                    description: `${tracks.track_A.name}: ${originalStep.description || ''}`,
                    globalPosition: index + trackIndex + 1,
                    extraction_type: originalStep.extraction_type || 'synthesis',
                    document_id: originalStep.document_id,
                    page_reference: originalStep.page_reference,
                    isTrackStep: true,
                    trackType: 'A',
                    originalStepId: trackStepId
                  };

                  allSteps.push(trackStep);
                  trackStepsCreated.add(`${trackStepId}_A`);

                  // Connect main decision step to first track step
                  if (trackIndex === 0) {
                    mainStep.connections.push(trackStep.id);
                  }
                }
              });
            }

            if (tracks.track_B?.next_steps) {
              const firstTrackBStepId = tracks.track_B.next_steps[0];
              const firstTrackBStep = findStepByReference(firstTrackBStepId, baseSteps);
              const trackBAssigneeForAll = decisionStepAssignee;
              const trackBFinalLane = decisionLaneIndex;

              tracks.track_B.next_steps.forEach((trackStepId: string, trackIndex: number) => {
                const originalStep = findStepByReference(trackStepId, baseSteps);

                // Skip creating track versions of steps that have their own decision_paths
                // These should remain as original decision steps, not track steps
                const isDecisionStep = originalStep?.decision_paths && (originalStep.decision_paths.yes || originalStep.decision_paths.no);

                if (originalStep && !trackStepsCreated.has(`${trackStepId}_B`) && !isDecisionStep) {
                  // Use TRACK B assignee for ALL steps in Track B (not individual step assignee)
                  const trackAssignee = trackBAssigneeForAll;
                  const finalLaneIndex = trackBFinalLane;

                  const trackStep = {
                    ...originalStep,  // ✅ Preserve ALL fields from database (including Visio fields)
                    id: `${originalStep.id}_TrackB`,
                    name: originalStep.name || `${tracks.track_B.name} - Step ${trackStepId}`,
                    type: 'process', // Force track steps to be process type
                    category: 'process',
                    action_type: 'process', // Force action_type to process to prevent decision processing
                    decision_paths: undefined, // Remove decision paths to prevent processing as decision
                    assignee: trackAssignee,
                    swimlane: trackAssignee,
                    position: {
                      x: index + trackIndex + 1,
                      y: trackBFinalLane
                    },
                    connections: (() => {
                      if (trackIndex < tracks.track_B.next_steps.length - 1) {
                        // Connect to next step in track
                        const nextTrackStep = baseSteps.find(s =>
                          s.step_number === tracks.track_B.next_steps[trackIndex + 1] ||
                          s.name?.includes(`Step ${tracks.track_B.next_steps[trackIndex + 1]} -`) ||
                          s.name?.includes(`Step ${tracks.track_B.next_steps[trackIndex + 1]}:`)
                        );
                        return nextTrackStep ? [`${nextTrackStep.id}_TrackB`] : [];
                      } else {
                        // Last step in track - connect to convergence point if specified (handles both "Step 2" and "2" formats)
                        const convergenceRef = tracks.track_B.convergence || tracks.convergence;
                        if (convergenceRef) {
                          const convergenceStep = findStepByReference(convergenceRef, baseSteps);
                          if (convergenceStep) {
                            const convergenceId = convergenceStep.id;
                            return [convergenceId];
                          }
                        }
                        // No further connections - track ends here
                        return [] as string[];
                      }
                    })(),
                    sources: originalStep.sources || [],
                    reasoning: originalStep.reasoning || '',
                    confidence_score: originalStep.confidence_score || 75,
                    full_text: originalStep.stepText || '',
                    description: `${tracks.track_B.name}: ${originalStep.description || ''}`,
                    globalPosition: index + trackIndex + 1,
                    extraction_type: originalStep.extraction_type || 'synthesis',
                    document_id: originalStep.document_id,
                    page_reference: originalStep.page_reference,
                    isTrackStep: true,
                    trackType: 'B',
                    originalStepId: trackStepId
                  };

                  allSteps.push(trackStep);
                  trackStepsCreated.add(`${trackStepId}_B`);

                  // Connect main decision step to first track step
                  if (trackIndex === 0) {
                    mainStep.connections.push(trackStep.id);
                  }
                }
              });
            }
          } else if (!hasParallelTracks) {
            // Regular step connection - handle parallel track transitions
            if (index < baseSteps.length - 1) {
              const nextStep = baseSteps[index + 1];
              const nextStepIsSkipped = trackStepsCreated.has(`${nextStep?.step_number}_A`) || trackStepsCreated.has(`${nextStep?.step_number}_B`);

              if (nextStepIsSkipped) {
                // Find the first non-skipped step after parallel tracks
                let connectionTarget = null;
                for (let j = index + 2; j < baseSteps.length; j++) {
                  const candidateStep = baseSteps[j];
                  const isSkipped = trackStepsCreated.has(`${candidateStep?.step_number}_A`) || trackStepsCreated.has(`${candidateStep?.step_number}_B`);
                  if (!isSkipped) {
                    connectionTarget = candidateStep.id;
                    break;
                  }
                }
                if (connectionTarget) {
                  mainStep.connections = [connectionTarget];
                }
              } else {
                const nextStepNum = (nextStep?.step_number || '').toString().toLowerCase();
                const isDecisionEndStep = nextStepNum.startsWith('end-');
                // Only skip an array-next "End-*" step if the CURRENT step is itself
                // a decision/gateway. For a decision, the End-* branch belongs to one
                // path (handled by decision_paths) and the convergence sits further
                // ahead, so we skip ahead. For a regular task whose actual successor
                // IS the End-* step (e.g. "Step 16A — Denial Notification Issued" →
                // "Step End-Denied"), we must draw that edge or End-* renders orphaned.
                const currentIsDecision = step?.is_decision || step?.bpmn_type === 'gateway' || step?.action_type === 'decision';
                if (isDecisionEndStep && currentIsDecision) {
                  let targetId = null;
                  for (let j = index + 2; j < baseSteps.length; j++) {
                    const candidate = baseSteps[j];
                    const candidateNum = (candidate?.step_number || '').toString().toLowerCase();
                    if (!candidateNum.startsWith('end-')) {
                      targetId = candidate.id;
                      break;
                    }
                  }
                  if (targetId) {
                    mainStep.connections = [targetId];
                  }
                } else {
                  const nextStepId = nextStep?.id || `step_${index + 1}`;
                  mainStep.connections = [nextStepId];
                }
              }
            }
          }
        });

        return allSteps;
      }

      case 4:
        return baseSteps.map((step, index) => {
          const { type, category } = categorizeStep(step, index, baseSteps.length);
          const stepText = step.name || step.step || '';
          const aiAssignedRole = step.actor || step.assignee;
          const assignedRole = aiAssignedRole && allRoles.includes(aiAssignedRole)
            ? aiAssignedRole
            : assignProcessToStakeholder(step.name || step.step || '', processes);
          const laneIndex = allRoles.indexOf(assignedRole ?? '');

          const decisionPaths = (type === 'decision' || type === 'split') ? generateDecisionPaths({
            ...step,
            type,
            name: stepText,
            id: step.id || index
          }, index, baseSteps) : undefined;

          return {
            id: step.id?.startsWith('L4_') ? step.id : `L4_${step.id || index}`,
            name: stepText,
            type: type,
            category: category,
            action_type: step.action_type || 'process',
            assignee: assignedRole,
            swimlane: assignedRole,
            position: { x: index, y: Math.max(0, laneIndex) },
            connections: index < baseSteps.length - 1 ? [
              baseSteps[index + 1]?.id?.startsWith('L4_')
                ? baseSteps[index + 1].id
                : `L4_${baseSteps[index + 1]?.id || (index + 1)}`
            ] : [],
            decisionPaths: decisionPaths,
            sources: step.sources || [],
            full_text: step.stepText || '',
            description: step.description || step.inference || '',
            globalPosition: index,
            reasoning: step.reasoning || '',
            confidence_score: step.confidence_score,
            extraction_type: step.extraction_type,
            document_id: step.document_id,
            page_reference: step.page_reference,
            isTrackStep: false
          };
        });
      default:
        return [];
    }
  };

  const finalProcessSteps = React.useMemo(() => {
    if (!processes || processes.length === 0) return [];

    const generatedSteps = generateLeveledProcesses();

    // Map steps to new filtered roles positions
    let processedSteps = generatedSteps.map((step) => {

      const swimlane = step.swimlane || step.assignee;
      const laneIdx = swimlane ? roles.indexOf(swimlane) : -1;
      const y = laneIdx >= 0 ? laneIdx : 0;

      return {
        ...step,
        position: {
          x: step.position.x,
          y
        }
      };
    });

    const endStepRepositions = new Map<string, { x: number; y: number; parentId: string; yOffset: number }>();

    // First, find all End steps
    const endSteps = processedSteps.filter(s => {
      const stepName = (s.name || '').toLowerCase();
      const stepNumber = (s.step_number || '').toString().toLowerCase();
      return stepName.startsWith('end') || stepNumber.startsWith('end');
    });

    // Track how many End steps are assigned to each parent decision for offset calculation
    const parentDecisionEndCount = new Map<string, number>();

    // For each End step, find its UNIQUE parent decision
    endSteps.forEach(endStep => {
      const endStepNumber = (endStep as any).step_number || '';
      const endStepId = endStep.id || '';
      const parentDecisionStepRef = (endStep as any).parent_decision_step;

      let parentDecision = null;

      // Priority 1: Check if End step has explicit parent_decision_step reference (from backend fix)
      if (parentDecisionStepRef) {
        parentDecision = processedSteps.find(s =>
          (s as any).step_number === parentDecisionStepRef ||
          s.id === parentDecisionStepRef ||
          s.id.includes(parentDecisionStepRef)
        );
      }

      // Priority 2: Check if End step number contains a decision step reference (e.g., "End-4" for Step 4)
      if (!parentDecision && endStepNumber) {
        const endStepMatch = endStepNumber.match(/End-(\d+[A-Z]?)/i);
        if (endStepMatch) {
          const decisionRef = endStepMatch[1];
          parentDecision = processedSteps.find(s =>
            (s as any).step_number === decisionRef ||
            s.id.includes(`step_${decisionRef}`) ||
            s.id.includes(`_${decisionRef}`)
          );
        }
      }

      // Priority 3: Find decision step that points to this End step via its 'no' path
      if (!parentDecision) {
        parentDecision = processedSteps.find(s => {
          const decisionPaths = (s as any).decision_paths || s.decisionPaths;
          if (!decisionPaths) return false;

          const noTarget = decisionPaths.no;
          if (noTarget) {
            if (noTarget === endStepNumber) return true;
            if (noTarget === endStepId) return true;
            if (endStepNumber && endStepNumber.toString() === noTarget) return true;
          }

          if (decisionPaths.no_path_details?.convergence === endStepNumber) return true;
          if (decisionPaths.no_path_details?.end_step_name) {
            const endName = (endStep as any).step_name || endStep.name || '';
            if (decisionPaths.no_path_details.end_step_name === endName) return true;
          }

          return false;
        });
      }

      if (parentDecision) {
        const parentId = parentDecision.id;
        const currentCount = parentDecisionEndCount.get(parentId) || 0;
        parentDecisionEndCount.set(parentId, currentCount + 1);
        const yOffset = currentCount * 0.5;

        const decisionPaths = (parentDecision as any).decision_paths || (parentDecision as any).decisionPaths;
        let yesTarget = decisionPaths?.yes ? processedSteps.find(s =>
          (s as any).step_number === decisionPaths.yes || s.id === decisionPaths.yes
        ) : null;

        let endX: number;
        let endY: number;

        if (yesTarget) {
          endX = yesTarget.position.x;
          endY = yesTarget.position.y + 1 + yOffset;
        } else {
          endX = parentDecision.position.x + 1;
          endY = parentDecision.position.y + 1 + yOffset;
        }

        endStepRepositions.set(endStep.id, {
          x: endX,
          y: endY,
          parentId: parentId,
          yOffset: yOffset
        });
      }
    });

    // Build a set of occupied positions from regular (non-End) steps
    const occupiedPositions = new Set<string>();
    processedSteps.forEach(step => {
      const stepName = (step.name || '').toLowerCase();
      const stepNumber = ((step as any).step_number || '').toString().toLowerCase();
      const isEndStep = stepName.startsWith('end') || stepNumber.startsWith('end');
      if (!isEndStep) {
        occupiedPositions.add(`${step.position.x},${step.position.y}`);
      }
    });

    // Also track positions claimed by End steps to avoid End-End overlaps
    const claimedByEndSteps = new Set<string>();

    // Adjust End step positions to avoid overlapping with regular steps
    endStepRepositions.forEach((reposition, endStepId) => {
      let targetX = reposition.x;
      let targetY = reposition.y;

      const nearbyYs = [Math.floor(targetY), Math.ceil(targetY)];
      let hasConflict = nearbyYs.some(checkY => occupiedPositions.has(`${targetX},${checkY}`));

      // Also check exact position against other End steps
      if (!hasConflict) {
        hasConflict = claimedByEndSteps.has(`${targetX},${targetY}`);
      }

      // If conflict, shift right one column at a time until a free spot is found
      while (hasConflict) {
        targetX += 1;
        const stillConflict = nearbyYs.some(checkY => occupiedPositions.has(`${targetX},${checkY}`)) ||
                              claimedByEndSteps.has(`${targetX},${targetY}`);
        if (!stillConflict) break;
      }

      claimedByEndSteps.add(`${targetX},${targetY}`);
      reposition.x = targetX;
      reposition.y = targetY;
    });

    // Apply End step repositioning
    if (endStepRepositions.size > 0) {
      processedSteps = processedSteps.map(step => {
        const reposition = endStepRepositions.get(step.id);
        if (reposition) {
          return {
            ...step,
            position: {
              x: reposition.x,
              y: reposition.y
            },
            parentDecisionId: reposition.parentId
          };
        }
        return step;
      });
    }

    if (currentMap?.audit_trail || currentMap?.map_data?.audit_trail) {
      const auditData = currentMap.audit_trail || currentMap.map_data.audit_trail;
      processedSteps = enrichStepsWithAuditData(processedSteps, auditData);
    }

    return processedSteps;
  }, [processes, selectedLevel, roles, analysisResults]);


  const levelConfig = {
    1: { laneHeight: 100, stepWidth: 120, stepHeight: 60, maxSteps: 5 },
    2: { laneHeight: 130, stepWidth: 140, stepHeight: 70, maxSteps: 8 },
    3: { laneHeight: 179, stepWidth: 160, stepHeight: 80, maxSteps: 12 },
    4: { laneHeight: 180, stepWidth: 180, stepHeight: 100, maxSteps: 20 }
  };

  const config = levelConfig[selectedLevel as keyof typeof levelConfig] || levelConfig[2];
  const { laneHeight: baseLaneHeight, stepWidth, stepHeight } = config;

  const getLaneHeightsMap = React.useMemo(() => {
    const laneHeights: { [key: number]: number } = {};
    const lanesWithParallelTracks = new Set<number>();
    const lanesWithDecisionDrops = new Set<number>();

    finalProcessSteps.forEach(step => {
      const isTrackStep = (step as any).isTrackStep;
      if (isTrackStep) {
        const laneIdx = Math.floor(step.position.y);
        lanesWithParallelTracks.add(laneIdx);
      }

      // Detect decision steps that have No-paths (these drop below the lane)
      const isDecision = step.type === 'decision' || step.type === 'split' ||
        step.category === 'Decision' || step.category === 'Decision Points';
      if (isDecision && step.decisionPaths) {
        const laneIdx = Math.floor(step.position.y);
        lanesWithDecisionDrops.add(laneIdx);
      }
    });

    roles.forEach((_, index) => {
      let height = baseLaneHeight;
      if (lanesWithParallelTracks.has(index)) {
        height = baseLaneHeight + 100;
      }
      if (lanesWithDecisionDrops.has(index)) {
        height = Math.max(height, baseLaneHeight + 80);
      }
      laneHeights[index] = height;
    });

    return laneHeights;
  }, [finalProcessSteps, roles, baseLaneHeight]);

  const getLaneHeight = (laneIndex: number) => {
    return getLaneHeightsMap[laneIndex] || baseLaneHeight;
  };

  const getTotalHeightUpToLane = (laneIndex: number) => {
    let total = 0;
    for (let i = 0; i < laneIndex; i++) {
      const isLastLane = i === roles.length - 1;
      total += isLastLane ? getLaneHeight(i) + 200 : getLaneHeight(i);
    }
    return total;
  };

  const laneHeight = baseLaneHeight;


  React.useEffect(() => {
    if (finalProcessSteps.length > 0 && onStatisticsUpdate) {
      const processCategories = calculateCategoryCount(finalProcessSteps);

      const statistics = {
        totalSteps: finalProcessSteps.length,
        complexityScore: calculateComplexityScore(finalProcessSteps, selectedLevel),
        stakeholders: roles.length,
        processCategories: processCategories,
        level: selectedLevel,
        finalProcessSteps: finalProcessSteps,
        actualStepCount: finalProcessSteps.length
      };

      onStatisticsUpdate(statistics);
    }
  }, [finalProcessSteps.length, selectedLevel, roles.length, onStatisticsUpdate]);



  const loadStepExceptionData = useCallback(async () => {
    // Check if we have exception analysis data in the currentMap
    const hasExceptionAnalysis = currentMap?.audit_trail?.exception_analysis_status === 'completed' &&
                                currentMap?.audit_trail?.exception_analysis?.detected_exceptions?.length > 0;

    if (!currentMap?.id) {
      return;
    }

    // Load exception data if we have exception analysis OR exception_coverage_score > 0
    if (!hasExceptionAnalysis && (!currentMap?.exception_coverage_score || currentMap.exception_coverage_score === 0)) {
      return;
    }

    try {
      const token = localStorage.getItem('jarvis_access_token');
      const response = await api.get(`/api/analysis/process-maps/${currentMap.id}/audit-trail`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Check exception analysis status from audit trail
      const exceptionAnalysisStatus = response.data?.audit_trail?.exception_analysis_status;
      const exceptionAnalysis = response.data?.audit_trail?.exception_analysis;

      if (exceptionAnalysisStatus === 'pending') {
        setStepExceptionData(new Map());
        return;
      }

      if (exceptionAnalysisStatus === 'failed') {
        setStepExceptionData(new Map());
        return;
      }

      // Check if analysis completed but no exceptions found
      if (exceptionAnalysisStatus === 'completed' &&
          exceptionAnalysis?.detected_exceptions &&
          exceptionAnalysis.detected_exceptions.length === 0) {
        setStepExceptionData(new Map());
        return;
      }

      // Get exception data from the main analysis and match to steps
      const stepTraceability = response.data?.audit_trail?.step_traceability || [];
      const detectedExceptions = exceptionAnalysis?.detected_exceptions || [];

      if (stepTraceability.length > 0 && detectedExceptions.length > 0) {
        const exceptionMap = new Map();

        stepTraceability.forEach((step: any) => {
          // Find exceptions that reference this step by name
          const relatedExceptions = detectedExceptions.filter((exception: any) =>
            exception.step_related && exception.step_related.some((stepRef: string) =>
              stepRef.toLowerCase().includes(step.final_step_name.toLowerCase().slice(0, 15)) ||
              step.final_step_name.toLowerCase().includes(stepRef.toLowerCase().slice(0, 15))
            )
          );

          if (relatedExceptions.length > 0) {

            // ✅ FR-001 FIX: Simplified step ID matching - IDs now match directly (task_uuid format)
            const matchingProcessStep = finalProcessSteps.find(processStep => {
              const backendStepId = step.step_id; // e.g., 'task_a1b2c3d4-e5f6-7890'
              const frontendStepId = processStep.id; // e.g., 'task_a1b2c3d4-e5f6-7890'

              // Direct exact match (primary - UUIDs should match exactly)
              if (frontendStepId === backendStepId) return true;

              // Extract UUID parts and compare (fallback for any prefix differences)
              const backendUUID = backendStepId.match(/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/i)?.[0];
              const frontendUUID = frontendStepId.match(/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/i)?.[0];
              if (backendUUID && frontendUUID && backendUUID === frontendUUID) return true;

              // Fallback: Remove all prefixes and compare
              const backendBase = backendStepId.replace(/^(L\d+_|activity_|task_|step_)/, '');
              const frontendBase = frontendStepId.replace(/^(L\d+_|activity_|task_|step_)/, '').replace(/_(TrackA|TrackB|Track\w+)$/, '');
              if (backendBase && frontendBase && backendBase === frontendBase) return true;

              // Handle Track-specific IDs: activity_24_TrackA should match activity_24
              if (frontendStepId.includes('_Track') && backendStepId.startsWith('activity_')) {
                const frontendBaseNumber = frontendStepId.replace(/^activity_(\d+)_Track\w+$/, '$1');
                const backendBaseNumber = backendStepId.replace(/^activity_(\d+)$/, '$1');
                if (frontendBaseNumber && backendBaseNumber && frontendBaseNumber === backendBaseNumber) return true;
              }

              return false;
            });

            if (matchingProcessStep) {
              const hasHighRiskExceptions = relatedExceptions.some((exc: any) =>
                exc.business_impact?.severity === 'high'
              );

              const totalExceptions = relatedExceptions.length;

              exceptionMap.set(matchingProcessStep.id, {
                stepData: {
                  step_name: step.final_step_name,
                  step_id: step.step_id,
                  potential_exceptions: relatedExceptions.map((exc: any) => ({
                    exception_type: exc.classification,
                    description: exc.description,
                    likelihood: exc.business_impact?.severity || 'medium',
                    business_impact: exc.business_impact?.description || 'Process disruption',
                    trigger_conditions: exc.trigger_conditions || [],
                    source_document: exc.source_document,
                    confidence_level: exc.confidence_level
                  })),
                  exception_paths: relatedExceptions.map((exc: any) => ({
                    trigger: exc.trigger_conditions?.join(' OR ') || 'Exception condition',
                    alternative_process: exc.recovery_procedure?.alternative_workflow || 'Standard recovery',
                    escalation_required: exc.recovery_procedure?.escalation_required || false,
                    escalation_to: exc.recovery_procedure?.escalation_to || 'Supervisor',
                    resolution_timeframe: exc.recovery_procedure?.resolution_timeframe || '5-10 minutes'
                  })),
                  standard_path: {
                    description: step.final_step_name,
                    controls: ['Document-based controls'],
                    approvals: ['Document-validated approvals']
                  }
                },
                hasExceptions: true,
                totalExceptions,
                totalExceptionProcesses: relatedExceptions.length,
                hasHighRisk: hasHighRiskExceptions,
                riskLevel: hasHighRiskExceptions ? 'high' : 'medium',
                enhanced: true,
                document_based: true,
                framework_compliant: true
              });
            }
          }
        });

        setStepExceptionData(exceptionMap);
      }
    } catch (error) {
      console.error('Failed to load step exception data:', error);
      setStepExceptionData(new Map());
    }
  }, [currentMap?.id, currentMap?.exception_coverage_score, finalProcessSteps]);


  useEffect(() => {
    // Trigger exception data loading if we have exception analysis OR exception_coverage_score > 0
    const hasExceptionAnalysis = currentMap?.audit_trail?.exception_analysis_status === 'completed' &&
                                currentMap?.audit_trail?.exception_analysis?.detected_exceptions?.length > 0;

    if (hasExceptionAnalysis || (currentMap?.exception_coverage_score && currentMap.exception_coverage_score > 0)) {
      loadStepExceptionData();
    }
  }, [loadStepExceptionData, currentMap?.audit_trail?.exception_analysis_status, currentMap?.audit_trail?.exception_analysis?.detected_exceptions?.length]);

  const calculateComplexityScore = (steps: any[], level: number) => {
    const baseScore = steps.length * 10;
    const levelMultiplier = level * 25;
    const decisionPoints = steps.filter(s =>
      s.type === 'decision' || s.type === 'split' || s.category === 'decision' ||
      (s.name || '').toLowerCase().includes('decide') ||
      (s.name || '').toLowerCase().includes('approve')
    ).length * 15;
    const criticalSteps = steps.filter(s => s.isCritical).length * 20;
    const handoffPoints = steps.filter(s => s.isHandoff).length * 10;

    return Math.min(100, baseScore + levelMultiplier + decisionPoints + criticalSteps + handoffPoints);
  };

  // Helper: Assign phase based on natural breakpoints (decisions, actor changes, step distribution)
  const assignPhase = (_step: any, index: number, allSteps: any[]): string => {
    try {
      if (!allSteps || allSteps.length === 0) return 'Phase 1: Main Process';

      const totalSteps = allSteps.length;
      const stepNumber = index + 1;

      // For small processes (<=15 steps), use single phase
      if (totalSteps <= 15) {
        return 'Phase 1: Main Process';
      }

      // For medium processes (16-30 steps), use 2-3 phases
      if (totalSteps <= 30) {
        if (stepNumber <= totalSteps * 0.5) {
          return 'Phase 1: Initial Processing';
        } else {
          return 'Phase 2: Completion';
        }
      }

      // For large processes (31-50 steps), use 3-4 phases
      if (totalSteps <= 50) {
        if (stepNumber <= totalSteps * 0.33) {
          return 'Phase 1: Initial Processing';
        } else if (stepNumber <= totalSteps * 0.67) {
          return 'Phase 2: Execution';
        } else {
          return 'Phase 3: Completion';
        }
      }

      // For very large processes (>50 steps), use 4-5 phases
      if (stepNumber <= totalSteps * 0.25) {
        return 'Phase 1: Discovery & Setup';
      } else if (stepNumber <= totalSteps * 0.5) {
        return 'Phase 2: Execution';
      } else if (stepNumber <= totalSteps * 0.75) {
        return 'Phase 3: Validation & Review';
      } else {
        return 'Phase 4: Completion & Closeout';
      }
    } catch (error) {
      console.warn('Error assigning phase:', error);
      return 'Phase 1: Main Process';
    }
  };

  // Helper: Generate Phase Summary data
  const generatePhaseSummary = (processData: any[]): any[] => {
    try {
      if (!processData || processData.length === 0) return [];

      // Group steps by phase
      const phaseGroups = new Map<string, any[]>();

      processData.forEach((step: any) => {
        const phase = step['Phase'] || 'Phase 1: Main Process';
        if (!phaseGroups.has(phase)) {
          phaseGroups.set(phase, []);
        }
        phaseGroups.get(phase)!.push(step);
      });

      // Create summary data
      const summary: any[] = [];

      phaseGroups.forEach((steps, phaseName) => {
        const stepNumbers = steps.map(s => s['Step Number'] || 0).filter(n => n > 0);
        if (stepNumbers.length === 0) return;

        const minStep = Math.min(...stepNumbers);
        const maxStep = Math.max(...stepNumbers);

        const actors = Array.from(new Set(steps.map(s => s['Swim Lane'] || s['Actor'] || 'Unknown').filter(a => a)));
        const decisions = steps.filter(s =>
          s['Visio Shape Type'] === 'Decision' ||
          s['Action Type'] === 'decision' ||
          s['Action Type'] === 'approval'
        ).length;

        const parallelFlows = steps.filter(s => s['Has Parallel Tracks'] === 'Yes').length;

        summary.push({
          'Phase Name': phaseName,
          'Step Range': minStep === maxStep ? `${minStep}` : `${minStep}-${maxStep}`,
          'Total Steps': steps.length,
          'Decision Points': decisions,
          'Parallel Flows': parallelFlows,
          'Unique Actors': actors.length,
          'Primary Actors': actors.slice(0, 4).join(', ') + (actors.length > 4 ? `, +${actors.length - 4} more` : ''),
          'Complexity': decisions > 3 ? 'High' : decisions > 1 ? 'Medium' : 'Low'
        });
      });

      return summary;
    } catch (error) {
      console.warn('Error generating phase summary:', error);
      return [];
    }
  };

  const downloadAsExcel = async (swimlaneRoles: string[], swimlaneSteps: ExtendedProcessStep[]) => {
    if (!swimlaneSteps || swimlaneSteps.length === 0) {
      alert('No swimlane data to export');
      return;
    }

    try {
      
      const getVisioShapeType = (step: any): string => {
        const actionType = ((step as any).action_type || '').toLowerCase();
        const bpmnType = ((step as any).bpmn_type || '').toLowerCase();
        const eventType = ((step as any).event_type || '').toLowerCase();
        const stepName = (step.name || '').toLowerCase();

        // Check for Start/End events first
        if (eventType === 'startevent' || actionType === 'start' || stepName.startsWith('start')) {
          return 'Start';
        }
        if (eventType === 'endevent' || actionType === 'end' || stepName.startsWith('end')) {
          return 'End';
        }

        // Check for Decision/Gateway
        if (bpmnType === 'gateway' || actionType === 'decision' || actionType === 'approval' ||
            actionType === 'review' || actionType === 'validation' || actionType === 'split') {
          return 'Decision';
        }

        // Check for Document-related
        if (actionType === 'documentation' || actionType === 'document') {
          return 'Document';
        }

        // Check for Data-related
        if (actionType === 'data' || actionType === 'database' || actionType === 'storage') {
          return 'Data';
        }

        // Check for Subprocess
        if (actionType === 'subprocess' || actionType === 'sub-process') {
          return 'Subprocess';
        }

        // Default to Process
        return 'Process';
      };

      // Helper: Find step by ID or step_number and return its step_number
      const getStepNumber = (stepRef: string): string => {
        if (!stepRef) return '';

        // Direct match by ID
        const foundStep = swimlaneSteps.find(s => s.id === stepRef);
        if (foundStep) {
          return String((foundStep as any).step_number || '');
        }

        // Match by step_number
        const foundByStepNum = swimlaneSteps.find(s => (s as any).step_number === stepRef);
        if (foundByStepNum) {
          return String((foundByStepNum as any).step_number || '');
        }

        // If already a valid step number, return as-is
        return stepRef;
      };

      const visioFlowchartData = swimlaneSteps.map((step, index) => {
        const currentStepId = String((step as any).step_number || index);
        const shapeType = getVisioShapeType(step);

        // Calculate Next Step ID(s) and Connector Label(s)
        let nextStepId = '';
        let connectorLabel = '';
        const nextStep = swimlaneSteps[index + 1];

        if (shapeType === 'Decision') {
          const stepDecisionPaths = (step as any).decision_paths || step.decisionPaths;

          if (stepDecisionPaths?.parallel_tracks) {
            // Parallel tracks: comma-separated IDs
            const tracks = stepDecisionPaths.parallel_tracks;
            const trackARef = tracks.track_A?.next_steps?.[0] || '';
            const trackBRef = tracks.track_B?.next_steps?.[0] || '';
            const trackAName = tracks.track_A?.name || 'Track A';
            const trackBName = tracks.track_B?.name || 'Track B';

            nextStepId = `${trackARef},${trackBRef}`;
            connectorLabel = `${trackAName},${trackBName}`;
          } else if (stepDecisionPaths?.yes || stepDecisionPaths?.no) {
            // Yes/No decision paths
            const yesPathRef = stepDecisionPaths?.yes || '';
            const noPathRef = stepDecisionPaths?.no || '';

            const yesStepNum = yesPathRef ? getStepNumber(yesPathRef) : (nextStep ? String((nextStep as any).step_number || '') : '');
            const noStepNum = noPathRef ? getStepNumber(noPathRef) : '';

            // Visio format: comma-separated, no spaces
            if (yesStepNum && noStepNum) {
              nextStepId = `${yesStepNum},${noStepNum}`;
              connectorLabel = `${stepDecisionPaths?.yes_condition || 'Yes'},${stepDecisionPaths?.no_condition || 'No'}`;
            } else if (yesStepNum) {
              nextStepId = yesStepNum;
              connectorLabel = stepDecisionPaths?.yes_condition || 'Yes';
            }
          } else if (nextStep) {
            nextStepId = String((nextStep as any).step_number || '');
          }
        } else if (shapeType !== 'End') {
          // Non-decision, non-end steps: sequential connection
          if (step.connections && Array.isArray(step.connections) && step.connections.length > 0) {
            nextStepId = getStepNumber(step.connections[0]);
          } else if (nextStep) {
            nextStepId = String((nextStep as any).step_number || '');
          }
        }

        // Return EXACT Visio Data Visualizer column names
        return {
          'Process Step ID': currentStepId,
          'Process Step Description': (step as any).hybrid_description || step.name || `Step ${currentStepId}`,
          'Next Step ID': nextStepId,
          'Connector Label': connectorLabel,
          'Shape Type': shapeType,
          'Function': (step as any).actor || step.swimlane || step.assignee || 'Default',
          'Phase': (step as any).phase || assignPhase(step, index, swimlaneSteps),
          'Alt Text': (step as any).detailed_description || step.description || ''
        };
      });

      // ============================================================
      // SHEET 2: Extended_Process_Data (Additional metadata for reference)
      // ============================================================
      const extendedProcessData = swimlaneSteps.map((step, index) => {
        const currentStepId = String((step as any).step_number || index);
        const stepDecisionPaths = (step as any).decision_paths || step.decisionPaths;
        const hasParallelTracks = stepDecisionPaths?.parallel_tracks ? 'Yes' : 'No';

        return {
          'Process Step ID': currentStepId,
          'Step Name': (step as any).hybrid_description || step.name || '',
          'Detailed Description': (step as any).detailed_description || step.description || step.full_text || '',
          'Actor/Function': (step as any).actor || step.swimlane || step.assignee || '',
          'BPMN Type': (step as any).bpmn_type || '',
          'Task Type': (step as any).task_type || '',
          'Gateway Type': (step as any).gateway_type || '',
          'Event Type': (step as any).event_type || '',
          'Has Parallel Tracks': hasParallelTracks,
          'Track A Name': stepDecisionPaths?.parallel_tracks?.track_A?.name || '',
          'Track A Steps': stepDecisionPaths?.parallel_tracks?.track_A?.next_steps?.join(', ') || '',
          'Track B Name': stepDecisionPaths?.parallel_tracks?.track_B?.name || '',
          'Track B Steps': stepDecisionPaths?.parallel_tracks?.track_B?.next_steps?.join(', ') || '',
          'Convergence Point': stepDecisionPaths?.parallel_tracks?.track_A?.convergence || stepDecisionPaths?.parallel_tracks?.track_B?.convergence || '',
          'Yes Condition': stepDecisionPaths?.yes_condition || '',
          'No Condition': stepDecisionPaths?.no_condition || '',
          'Duration (Hours)': (step as any).duration || '',
          'Systems Involved': (step as any).systems_involved ? (step as any).systems_involved.join(', ') : '',
          'Inputs': (step as any).inputs ? (step as any).inputs.join(', ') : '',
          'Outputs': (step as any).outputs ? (step as any).outputs.join(', ') : '',
          'Decision Criteria': (step as any).business_criteria || (step as any).decision_criteria || '',
          'Source Citations': (step as any).source_citations ? (step as any).source_citations.join('; ') : '',
          'Documentation': (step as any).documentation || '',
          'Confidence Score': (step as any).confidence_score || '',
          'Parent Decision Step': (step as any).parent_decision_step || ''
        };
      });

      const uniqueFunctions = Array.from(new Set(visioFlowchartData.map(s => s['Function']).filter(Boolean)));
      const swimlaneSummaryData = uniqueFunctions.map(func => {
        const funcSteps = visioFlowchartData.filter(s => s['Function'] === func);
        const decisions = funcSteps.filter(s => s['Shape Type'] === 'Decision').length;
        const starts = funcSteps.filter(s => s['Shape Type'] === 'Start').length;
        const ends = funcSteps.filter(s => s['Shape Type'] === 'End').length;

        return {
          'Function/Swimlane': func,
          'Total Steps': funcSteps.length,
          'Process Steps': funcSteps.filter(s => s['Shape Type'] === 'Process').length,
          'Decision Points': decisions,
          'Start Events': starts,
          'End Events': ends,
          'First Step ID': funcSteps[0]?.['Process Step ID'] || '',
          'Last Step ID': funcSteps[funcSteps.length - 1]?.['Process Step ID'] || ''
        };
      });

      const uniquePhases = Array.from(new Set(visioFlowchartData.map(s => s['Phase']).filter(Boolean)));
      const phaseSummaryData = uniquePhases.map(phase => {
        const phaseSteps = visioFlowchartData.filter(s => s['Phase'] === phase);
        const phaseActors = Array.from(new Set(phaseSteps.map(s => s['Function']).filter(Boolean)));
        const phaseDecisions = phaseSteps.filter(s => s['Shape Type'] === 'Decision').length;

        return {
          'Phase': phase,
          'Total Steps': phaseSteps.length,
          'Functions Involved': phaseActors.join(', '),
          'Decision Points': phaseDecisions,
          'First Step ID': phaseSteps[0]?.['Process Step ID'] || '',
          'Last Step ID': phaseSteps[phaseSteps.length - 1]?.['Process Step ID'] || ''
        };
      });

      // ============================================================
      // SHEET 5: Decision_Paths (All decision branches detailed)
      // ============================================================
      const decisionPathsData = swimlaneSteps
        .filter(step => {
          const stepDecisionPaths = (step as any).decision_paths || step.decisionPaths;
          return stepDecisionPaths && (stepDecisionPaths.yes || stepDecisionPaths.no || stepDecisionPaths.parallel_tracks);
        })
        .map(step => {
          const currentStepId = String((step as any).step_number || '');
          const stepDecisionPaths = (step as any).decision_paths || step.decisionPaths;
          const hasParallelTracks = stepDecisionPaths?.parallel_tracks ? 'Yes' : 'No';

          return {
            'Decision Step ID': currentStepId,
            'Decision Name': (step as any).hybrid_description || step.name || '',
            'Function': (step as any).actor || step.swimlane || step.assignee || '',
            'Phase': (step as any).phase || '',
            'Has Parallel Tracks': hasParallelTracks,
            'Yes Path Target': stepDecisionPaths?.yes || '',
            'Yes Condition': stepDecisionPaths?.yes_condition || 'Yes',
            'No Path Target': stepDecisionPaths?.no || '',
            'No Condition': stepDecisionPaths?.no_condition || 'No',
            'Track A Name': stepDecisionPaths?.parallel_tracks?.track_A?.name || '',
            'Track A First Step': stepDecisionPaths?.parallel_tracks?.track_A?.next_steps?.[0] || '',
            'Track B Name': stepDecisionPaths?.parallel_tracks?.track_B?.name || '',
            'Track B First Step': stepDecisionPaths?.parallel_tracks?.track_B?.next_steps?.[0] || '',
            'Convergence Point': stepDecisionPaths?.parallel_tracks?.track_A?.convergence || stepDecisionPaths?.parallel_tracks?.track_B?.convergence || ''
          };
        });

      // ============================================================
      // CREATE WORKBOOK
      // ============================================================
      const workbook = XLSX.utils.book_new();

      // Sheet 1: Visio_Cross_Functional_Flowchart (PRIMARY - Use this for Visio import)
      const visioSheet = XLSX.utils.json_to_sheet(visioFlowchartData);
      visioSheet['!cols'] = [
        { wch: 18 },  // Process Step ID
        { wch: 60 },  // Process Step Description
        { wch: 15 },  // Next Step ID
        { wch: 25 },  // Connector Label
        { wch: 15 },  // Shape Type
        { wch: 30 },  // Function
        { wch: 25 },  // Phase
        { wch: 80 }   // Alt Text
      ];
      XLSX.utils.book_append_sheet(workbook, visioSheet, 'Visio_Cross_Functional');

      // Sheet 2: Extended_Process_Data
      const extendedSheet = XLSX.utils.json_to_sheet(extendedProcessData);
      extendedSheet['!cols'] = [
        { wch: 18 }, { wch: 50 }, { wch: 80 }, { wch: 30 }, { wch: 12 },
        { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 18 }, { wch: 25 },
        { wch: 30 }, { wch: 25 }, { wch: 30 }, { wch: 18 }, { wch: 20 },
        { wch: 20 }, { wch: 15 }, { wch: 40 }, { wch: 40 }, { wch: 40 },
        { wch: 50 }, { wch: 80 }, { wch: 60 }, { wch: 15 }, { wch: 20 }
      ];
      XLSX.utils.book_append_sheet(workbook, extendedSheet, 'Extended_Process_Data');

      // Sheet 3: Swimlane_Summary
      const swimlaneSheet = XLSX.utils.json_to_sheet(swimlaneSummaryData);
      swimlaneSheet['!cols'] = [
        { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 18 }, { wch: 15 },
        { wch: 12 }, { wch: 15 }, { wch: 15 }
      ];
      XLSX.utils.book_append_sheet(workbook, swimlaneSheet, 'Swimlane_Summary');

      // Sheet 4: Phase_Summary
      const phaseSheet = XLSX.utils.json_to_sheet(phaseSummaryData);
      phaseSheet['!cols'] = [
        { wch: 25 }, { wch: 15 }, { wch: 40 }, { wch: 18 }, { wch: 15 }, { wch: 15 }
      ];
      XLSX.utils.book_append_sheet(workbook, phaseSheet, 'Phase_Summary');

      // Sheet 5: Decision_Paths
      const decisionSheet = XLSX.utils.json_to_sheet(decisionPathsData);
      decisionSheet['!cols'] = [
        { wch: 18 }, { wch: 50 }, { wch: 30 }, { wch: 25 }, { wch: 18 },
        { wch: 18 }, { wch: 20 }, { wch: 18 }, { wch: 20 }, { wch: 25 },
        { wch: 20 }, { wch: 25 }, { wch: 20 }, { wch: 18 }
      ];
      XLSX.utils.book_append_sheet(workbook, decisionSheet, 'Decision_Paths');

      // ============================================================
      // EXPORT FILE
      // ============================================================
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `Visio_Process_Map_Level${selectedLevel}_${timestamp}.xlsx`;

      await XLSX.writeFile(workbook, filename);

      const decisionCount = visioFlowchartData.filter(s => s['Shape Type'] === 'Decision').length;
      const swimlaneCount = uniqueFunctions.length;

      toast.success(
        `Visio-compatible Excel exported! ${visioFlowchartData.length} steps, ${decisionCount} decisions, ${swimlaneCount} swimlanes`,
        {
          duration: 5000,
          position: 'top-right',
          icon: '📊',
        }
      );

    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Error exporting to Excel. Please try again.', {
        duration: 4000,
        position: 'top-right',
      });
    }
  };

  const downloadAsPNG = async () => {
    if (!containerRef.current || isDownloading) return;

    setIsDownloading(true);
    try {
      const svgElement = containerRef.current.querySelector('svg');
      if (!svgElement) {
        throw new Error('SVG element not found');
      }

      const svgRect = svgElement.getBoundingClientRect();
      const actualSvgWidth = svgRect.width;
      const actualSvgHeight = svgRect.height;

      const mainContainer = containerRef.current.closest('.bg-white.rounded-lg.border.overflow-hidden') ||
        containerRef.current.parentElement?.parentElement;

      if (!mainContainer) {
        throw new Error('Could not find main container');
      }

      const roleLabelsWidth = 160;
      const exportWidth = roleLabelsWidth + actualSvgWidth + 50;
      const exportHeight = Math.max(actualSvgHeight, 600) + 150;

      const mainContainerEl = mainContainer as HTMLElement;
      const originalMainStyles = {
        overflow: mainContainerEl.style.overflow,
        height: mainContainerEl.style.height,
        maxHeight: mainContainerEl.style.maxHeight,
        width: mainContainerEl.style.width
      };

      (mainContainer as HTMLElement).style.overflow = 'visible';
      (mainContainer as HTMLElement).style.height = 'auto';
      (mainContainer as HTMLElement).style.maxHeight = 'none';
      (mainContainer as HTMLElement).style.width = `${exportWidth}px`;

      const scrollableArea = containerRef.current.parentElement;
      const originalScrollStyles = {
        overflow: scrollableArea?.style.overflow,
        overflowX: scrollableArea?.style.overflowX,
        overflowY: scrollableArea?.style.overflowY,
        width: scrollableArea?.style.width,
        maxWidth: scrollableArea?.style.maxWidth
      };

      if (scrollableArea) {
        scrollableArea.style.overflow = 'visible';
        scrollableArea.style.overflowX = 'visible';
        scrollableArea.style.overflowY = 'visible';
        scrollableArea.style.width = `${actualSvgWidth + 50}px`;
        scrollableArea.style.maxWidth = 'none';
      }

      const originalContainerStyles = {
        width: containerRef.current.style.width,
        height: containerRef.current.style.height,
        overflow: containerRef.current.style.overflow
      };

      containerRef.current.style.width = `${actualSvgWidth + 50}px`;
      containerRef.current.style.height = `${actualSvgHeight + 50}px`;
      containerRef.current.style.overflow = 'visible';

      await new Promise(resolve => setTimeout(resolve, 1000));

      const canvas = await html2canvas(mainContainer as HTMLElement, {
        backgroundColor: '#ffffff',
        scale: 1,
        useCORS: true,
        allowTaint: true,
        scrollX: 0,
        scrollY: 0,
        width: exportWidth,
        height: exportHeight,
        windowWidth: exportWidth + 100,
        windowHeight: exportHeight + 100,
        logging: false,
        removeContainer: false,
        ignoreElements: (element) => {
          return element.tagName === 'SCROLLBAR' ||
            element.classList?.contains('scrollbar') ||
            (element as HTMLElement).style?.position === 'fixed' ||
            (element as HTMLElement).style?.zIndex === '9999';
        }
      });

      Object.entries(originalMainStyles).forEach(([prop, value]) => {
        if (value !== undefined && value !== null) {
          ((mainContainer as HTMLElement).style as any)[prop] = value;
        } else {
          ((mainContainer as HTMLElement).style as any)[prop] = '';
        }
      });

      if (scrollableArea) {
        Object.entries(originalScrollStyles).forEach(([prop, value]) => {
          if (value !== undefined && value !== null) {
            (scrollableArea.style as any)[prop] = value;
          } else {
            (scrollableArea.style as any)[prop] = '';
          }
        });
      }

      if (containerRef.current) {
        Object.entries(originalContainerStyles).forEach(([prop, value]) => {
          if (containerRef.current) {
            if (value !== undefined && value !== null) {
              if (containerRef.current) {
                (containerRef.current.style as any)[prop] = value;
              }
            } else {
              (containerRef.current.style as any)[prop] = '';
            }
          }
        });
      }

      const link = document.createElement('a');
      link.download = `process-swimlanes-level${selectedLevel}-complete-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png', 1.0);
      link.click();

    } catch (error) {
      console.error('Error downloading PNG:', error);
      alert('Error downloading image. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadAsPDF = async () => {
    if (!containerRef.current || isDownloading) return;

    setIsDownloading(true);
    try {
      const svgElement = containerRef.current.querySelector('svg');
      if (!svgElement) {
        throw new Error('SVG element not found');
      }

      const svgRect = svgElement.getBoundingClientRect();
      const actualSvgWidth = svgRect.width;
      const actualSvgHeight = svgRect.height;

      const mainContainer = containerRef.current.closest('.bg-white.rounded-lg.border.overflow-hidden') ||
        containerRef.current.parentElement?.parentElement;

      if (!mainContainer) {
        throw new Error('Could not find main container');
      }

      const roleLabelsWidth = 160;
      const exportWidth = roleLabelsWidth + actualSvgWidth + 50;
      const exportHeight = Math.max(actualSvgHeight, 600) + 150;

      const originalMainStyles = {
        overflow: (mainContainer as HTMLElement).style.overflow,
        height: (mainContainer as HTMLElement).style.height,
        maxHeight: (mainContainer as HTMLElement).style.maxHeight,
        width: (mainContainer as HTMLElement).style.width
      };

      (mainContainer as HTMLElement).style.overflow = 'visible';
      (mainContainer as HTMLElement).style.height = 'auto';
      (mainContainer as HTMLElement).style.maxHeight = 'none';
      (mainContainer as HTMLElement).style.width = `${exportWidth}px`;

      const scrollableArea = containerRef.current.parentElement;
      const originalScrollStyles = {
        overflow: scrollableArea?.style.overflow,
        overflowX: scrollableArea?.style.overflowX,
        overflowY: scrollableArea?.style.overflowY,
        width: scrollableArea?.style.width,
        maxWidth: scrollableArea?.style.maxWidth
      };

      if (scrollableArea) {
        scrollableArea.style.overflow = 'visible';
        scrollableArea.style.overflowX = 'visible';
        scrollableArea.style.overflowY = 'visible';
        scrollableArea.style.width = `${actualSvgWidth + 50}px`;
        scrollableArea.style.maxWidth = 'none';
      }

      const originalContainerStyles = {
        width: containerRef.current.style.width,
        height: containerRef.current.style.height,
        overflow: containerRef.current.style.overflow
      };

      containerRef.current.style.width = `${actualSvgWidth + 50}px`;
      containerRef.current.style.height = `${actualSvgHeight + 50}px`;
      containerRef.current.style.overflow = 'visible';

      await new Promise(resolve => setTimeout(resolve, 1000));

      const canvas = await html2canvas(mainContainer as HTMLElement, {
        backgroundColor: '#ffffff',
        scale: 1,
        useCORS: true,
        allowTaint: true,
        scrollX: 0,
        scrollY: 0,
        width: exportWidth,
        height: exportHeight,
        windowWidth: exportWidth + 100,
        windowHeight: exportHeight + 100,
        logging: false,
        removeContainer: false,
        ignoreElements: (element) => {
          return element.tagName === 'SCROLLBAR' ||
            element.classList?.contains('scrollbar') ||
            (element as HTMLElement).style?.position === 'fixed' ||
            (element as HTMLElement).style?.zIndex === '9999';
        }
      });

      Object.entries(originalMainStyles).forEach(([prop, value]) => {
        if (value !== undefined && value !== null) {
          ((mainContainer as HTMLElement).style as any)[prop] = value;
        } else {
          ((mainContainer as HTMLElement).style as any)[prop] = '';
        }
      });

      if (scrollableArea) {
        Object.entries(originalScrollStyles).forEach(([prop, value]) => {
          if (value !== undefined && value !== null) {
            (scrollableArea.style as any)[prop] = value;
          } else {
            (scrollableArea.style as any)[prop] = '';
          }
        });
      }

      if (containerRef.current) {
        Object.entries(originalContainerStyles).forEach(([prop, value]) => {
          if (value !== undefined && value !== null) {
            //@ts-expect-error
            (containerRef.current.style as any)[prop] = value;
          } else {
            //@ts-expect-error
            (containerRef.current.style as any)[prop] = '';
          }
        });
      }



      const imgData = canvas.toDataURL('image/png', 1.0);

      const pdf = new jsPDF({
        orientation: exportWidth > exportHeight ? 'landscape' : 'portrait',
        unit: 'px',
        format: [Math.max(exportWidth, 800), Math.max(exportHeight, 600)]
      });

      pdf.addImage(imgData, 'PNG', 0, 0, exportWidth, exportHeight);
      pdf.save(`process-swimlanes-level${selectedLevel}-complete-${new Date().toISOString().split('T')[0]}.pdf`);

    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error downloading PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const exportToPowerPoint = async () => {
    setIsExporting(true);
    try {
      const svgElement = containerRef.current?.querySelector('svg');
      if (!svgElement || !containerRef.current) {
        throw new Error('Diagram not found');
      }

      const svgRect = svgElement.getBoundingClientRect();
      const actualSvgWidth = svgRect.width;
      const actualSvgHeight = svgRect.height;

      const mainContainer = containerRef.current.closest('.bg-white.rounded-lg.border.overflow-hidden') ||
        containerRef.current.parentElement?.parentElement;

      if (!mainContainer) {
        throw new Error('Could not find main container');
      }

      const roleLabelsWidth = 160;
      const exportWidth = roleLabelsWidth + actualSvgWidth + 50;
      const exportHeight = Math.max(actualSvgHeight, 600) + 150;

      const originalMainStyles = {
        overflow: (mainContainer as HTMLElement).style.overflow,
        height: (mainContainer as HTMLElement).style.height,
        maxHeight: (mainContainer as HTMLElement).style.maxHeight,
        width: (mainContainer as HTMLElement).style.width
      };

      (mainContainer as HTMLElement).style.overflow = 'visible';
      (mainContainer as HTMLElement).style.height = 'auto';
      (mainContainer as HTMLElement).style.maxHeight = 'none';
      (mainContainer as HTMLElement).style.width = `${exportWidth}px`;

      const scrollableArea = containerRef.current.parentElement;
      const originalScrollStyles = {
        overflow: scrollableArea?.style.overflow,
        overflowX: scrollableArea?.style.overflowX,
        overflowY: scrollableArea?.style.overflowY,
        width: scrollableArea?.style.width,
        maxWidth: scrollableArea?.style.maxWidth
      };

      if (scrollableArea) {
        scrollableArea.style.overflow = 'visible';
        scrollableArea.style.overflowX = 'visible';
        scrollableArea.style.overflowY = 'visible';
        scrollableArea.style.width = `${actualSvgWidth + 50}px`;
        scrollableArea.style.maxWidth = 'none';
      }

      const originalContainerStyles = {
        width: containerRef.current.style.width,
        height: containerRef.current.style.height,
        overflow: containerRef.current.style.overflow
      };

      containerRef.current.style.width = `${actualSvgWidth + 50}px`;
      containerRef.current.style.height = `${actualSvgHeight + 50}px`;
      containerRef.current.style.overflow = 'visible';

      await new Promise(resolve => setTimeout(resolve, 1000));

      const canvas = await html2canvas(mainContainer as HTMLElement, {
        backgroundColor: '#ffffff',
        scale: 1.0,
        useCORS: true,
        allowTaint: true,
        scrollX: 0,
        scrollY: 0,
        width: exportWidth,
        height: exportHeight,
        windowWidth: exportWidth + 100,
        windowHeight: exportHeight + 100
      });

      Object.entries(originalMainStyles).forEach(([prop, value]) => {
        if (value !== undefined && value !== null) {
          ((mainContainer as HTMLElement).style as any)[prop] = value;
        } else {
          ((mainContainer as HTMLElement).style as any)[prop] = '';
        }
      });

      if (scrollableArea) {
        Object.entries(originalScrollStyles).forEach(([prop, value]) => {
          if (value !== undefined && value !== null) {
            (scrollableArea.style as any)[prop] = value;
          } else {
            (scrollableArea.style as any)[prop] = '';
          }
        });
      }

      Object.entries(originalContainerStyles).forEach(([prop, value]) => {
        if (containerRef.current) {
          if (value !== undefined && value !== null) {
            (containerRef.current.style as any)[prop] = value;
          } else {
            (containerRef.current.style as any)[prop] = '';
          }
        }
      });

      const imageBlob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob!);
        }, 'image/png', 0.9);
      });

      const token = localStorage.getItem('jarvis_access_token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const projectId = currentProject?.id;
      if (!projectId) {
        toast.error('No project selected');
        return;
      }

      const formData = new FormData();
      formData.append('diagram_image', imageBlob, 'process-diagram-complete.png');

      const metadata = {
        title: `Process Map - Level ${selectedLevel}`,
        level: selectedLevel,
        totalSteps: finalProcessSteps.length,
        stakeholders: roles.length,
        export_type: 'swimlanes_diagram_export'
      };

      formData.append('metadata', JSON.stringify(metadata));

      const response = await api.post(`/api/export/swimlanes-ppt/${projectId}`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      const result = response.data;
      toast.success('PowerPoint exported successfully');

      if (result.download_url) {
        const fileResponse = await api.get(result.download_url, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        });

        const blob = fileResponse.data;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = result.filename || `Process_Diagram_Level${selectedLevel}_Complete_${new Date().toISOString().split('T')[0]}.pptx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }

    } catch (error) {
      console.error('PowerPoint export error:', error);
      toast.error(`Failed to export PowerPoint: ${error}`);
    } finally {
      setIsExporting(false);
    }
  };


  const renderValidConnections = () => {
    if (!finalProcessSteps || finalProcessSteps.length === 0) {
      return null;
    }

    // Clean rendering start

    const validConnections: JSX.Element[] = [];

    const sequenceFlows =
      (currentMap?.map_data?.elements?.sequence_flows) ||
      (analysisResults as any)?.process_synthesis?.sequence_flows ||
      [];
    const stepById = new Map<string, ExtendedProcessStep>();
    finalProcessSteps.forEach((s: ExtendedProcessStep) => stepById.set(s.id, s));

    const modelEdges = buildEdges(finalProcessSteps as any, sequenceFlows as any);
    modelEdges.forEach((edge) => {
      const from = stepById.get(edge.sourceId);
      const to = stepById.get(edge.targetId);
      if (!from || !to) return;
      const connector = renderConnector(from as any, to as any, edge);
      if (connector) {
        validConnections.push(React.cloneElement(connector, { key: `edge-${edge.id}` }));
      }
    });

    return validConnections.filter((c, i, a) => a.findIndex(x => x.key === c.key) === i);
  };



  const renderCurvedDecisionPath = (
    from: ExtendedProcessStep,
    to: ExtendedProcessStep,
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
    color: string,
    strokeDasharray: string,
    arrowMarker: string,
    decisionLabel: string,
    connectionType: string | undefined,
    selectedLevel: number,
    isDecisionStep: boolean,
    labelColor: string = '#1F2937' // ✅ NEW: Label color parameter
  ) => {
    // decisionLabel is supplied by the caller from edge.label (the data) — no
    // re-derivation here. Only fall back to a track name if the caller passed
    // nothing, so a labeled edge always wins.
    const isParallelTracks = from.decisionPaths?.parallel_tracks;
    if (!decisionLabel && isParallelTracks) {
      if (connectionType === 'yes') decisionLabel = from.decisionPaths?.parallel_tracks?.track_A?.name || '';
      else if (connectionType === 'no') decisionLabel = from.decisionPaths?.parallel_tracks?.track_B?.name || '';
    }
    const isRevisionLoop = to.name?.includes('Revision') || (to as any).step_number?.includes('A');
    const isYesPath = connectionType === 'yes';
    const isNoPath = connectionType === 'no';
    const isSelfLoop = from.id === to.id;

    if (isSelfLoop) {
      const loopOffset = 80;
      const loopHeight = 60;
      const topY = fromY - loopHeight;

      return (
        <g key={`${from.id}-${to.id}-selfloop`}>
          <line
            x1={fromX}
            y1={fromY}
            x2={fromX + loopOffset}
            y2={fromY}
            stroke={color}
            strokeWidth="2"
            strokeDasharray={strokeDasharray}
          />
          <line
            x1={fromX + loopOffset}
            y1={fromY}
            x2={fromX + loopOffset}
            y2={topY}
            stroke={color}
            strokeWidth="2"
            strokeDasharray={strokeDasharray}
          />
          <line
            x1={fromX + loopOffset}
            y1={topY}
            x2={fromX - loopOffset}
            y2={topY}
            stroke={color}
            strokeWidth="2"
            strokeDasharray={strokeDasharray}
          />
          <line
            x1={fromX - loopOffset}
            y1={topY}
            x2={fromX - loopOffset}
            y2={fromY}
            stroke={color}
            strokeWidth="2"
            strokeDasharray={strokeDasharray}
          />
          <line
            x1={fromX - loopOffset}
            y1={fromY}
            x2={fromX - 150}
            y2={fromY}
            stroke={color}
            strokeWidth="2"
            strokeDasharray={strokeDasharray}
            markerEnd={arrowMarker}
          />
          {isDecisionStep && decisionLabel && selectedLevel >= 2 && (
            <text
              x={fromX}
              y={topY - 10}
              fontSize="12"
              fill={labelColor}
              fontWeight="bold"
              textAnchor="middle"
              className="decision-label"
            >
              {decisionLabel}
            </text>
          )}
        </g>
      );
    }

    if ((from.name?.includes('Revision') || (from as any).step_number?.includes('A')) && to.position.x < from.position.x) {
      const upOffset = 60;
      const topY = fromY - upOffset;
      const midX = fromX + (toX - fromX) / 2;

      return (
        <g key={`${from.id}-${to.id}`}>
          {/* Right from revision step */}
          <line
            x1={fromX}
            y1={fromY}
            x2={fromX + 50}
            y2={fromY}
            stroke={color}
            strokeWidth="2"
            strokeDasharray={strokeDasharray}
          />

          {/* Up */}
          <line
            x1={fromX + 50}
            y1={fromY}
            x2={fromX + 50}
            y2={topY}
            stroke={color}
            strokeWidth="2"
            strokeDasharray={strokeDasharray}
          />

          {/* Left across the top */}
          <line
            x1={fromX + 50}
            y1={topY}
            x2={toX - 20}
            y2={topY}
            stroke={color}
            strokeWidth="2"
            strokeDasharray={strokeDasharray}
          />

          {/* Down to target */}
          <line
            x1={toX - 20}
            y1={topY}
            x2={toX}
            y2={toY}
            stroke={color}
            strokeWidth="2"
            strokeDasharray={strokeDasharray}
            markerEnd={arrowMarker}
          />

          {isDecisionStep && decisionLabel && selectedLevel >= 2 && (
            <text
              x={midX}
              y={topY - 10}
              fontSize="12"
              fill={labelColor}
              fontWeight="bold"
              textAnchor="middle"
              className="decision-label"
            >
              {decisionLabel}
            </text>
          )}
        </g>
      );
    }

    if (isNoPath) {
      const fromBoxX = from.position.x * (stepWidth + 50) + 30;
      const fromBoxY = selectedLevel === 1 ? 50 : (from.position.y * laneHeight + 50);
      const fromCenterX = fromBoxX + stepWidth / 2;
      const fromCenterY = fromBoxY + stepHeight / 2;
      const fromBottom = fromBoxY + stepHeight;
      const fromRightEdge = fromBoxX + stepWidth + 5;

      const toBoxX = to.position.x * (stepWidth + 50) + 30;
      const toBoxY = selectedLevel === 1 ? 50 : (to.position.y * laneHeight + 50);
      const toCenterY = toBoxY + stepHeight / 2;
      const toLeftEdge = toBoxX - 10;

      const isForwardNoPath = toX >= fromX;
      const isSameColumn = Math.abs(toBoxX - fromBoxX) < 20;

      // Same column: target is directly below/above — straight vertical line
      if (isSameColumn && Math.abs(fromY - toY) > 20) {
        const startY = toY > fromY ? fromBottom : fromBoxY;
        const endY = toY > fromY ? toBoxY : toBoxY + stepHeight;
        return (
          <g key={`${from.id}-${to.id}-no`}>
            <line
              x1={fromCenterX}
              y1={startY}
              x2={fromCenterX}
              y2={endY}
              stroke={color}
              strokeWidth="2"
              strokeDasharray={strokeDasharray}
              markerEnd={arrowMarker}
            />
            {isDecisionStep && decisionLabel && selectedLevel >= 2 && (
              <text
                x={fromCenterX + 15}
                y={(startY + endY) / 2}
                fontSize="12"
                fill={labelColor}
                fontWeight="bold"
                className="decision-label"
              >
                {decisionLabel}
              </text>
            )}
          </g>
        );
      }

      if (isForwardNoPath) {
        // Forward No path: route directly from decision to target (right then down/up then right)
        const isSameLane = Math.abs(fromY - toY) < 20;

        if (isSameLane) {
          // Same lane forward: straight horizontal dashed line
          return (
            <g key={`${from.id}-${to.id}-no`}>
              <line
                x1={fromRightEdge}
                y1={fromCenterY}
                x2={toLeftEdge}
                y2={toCenterY}
                stroke={color}
                strokeWidth="2"
                strokeDasharray={strokeDasharray}
                markerEnd={arrowMarker}
              />
              {isDecisionStep && decisionLabel && selectedLevel >= 2 && (
                <text
                  x={(fromRightEdge + toLeftEdge) / 2}
                  y={fromCenterY - 10}
                  fontSize="12"
                  fill={labelColor}
                  fontWeight="bold"
                  textAnchor="middle"
                  className="decision-label"
                >
                  {decisionLabel}
                </text>
              )}
            </g>
          );
        } else {
         
          const midX = fromRightEdge + 25;

          return (
            <g key={`${from.id}-${to.id}-no`}>
              <line
                x1={fromRightEdge}
                y1={fromCenterY}
                x2={midX}
                y2={fromCenterY}
                stroke={color}
                strokeWidth="2"
                strokeDasharray={strokeDasharray}
              />
              <line
                x1={midX}
                y1={fromCenterY}
                x2={midX}
                y2={toCenterY}
                stroke={color}
                strokeWidth="2"
                strokeDasharray={strokeDasharray}
              />
              <line
                x1={midX}
                y1={toCenterY}
                x2={toLeftEdge}
                y2={toCenterY}
                stroke={color}
                strokeWidth="2"
                strokeDasharray={strokeDasharray}
                markerEnd={arrowMarker}
              />
              {isDecisionStep && decisionLabel && selectedLevel >= 2 && (
                <text
                  x={midX + 10}
                  y={(fromCenterY + toCenterY) / 2}
                  fontSize="12"
                  fill={labelColor}
                  fontWeight="bold"
                  className="decision-label"
                >
                  {decisionLabel}
                </text>
              )}
            </g>
          );
        }
      } else {
        // Backward No path: drop below decision, loop back, come up to target
        const dropDistance = 100;
        const loopBottomY = fromBottom + dropDistance;

        return (
          <g key={`${from.id}-${to.id}-no`}>
            <line
              x1={fromCenterX}
              y1={fromBottom}
              x2={fromCenterX}
              y2={loopBottomY}
              stroke={color}
              strokeWidth="2"
              strokeDasharray={strokeDasharray}
            />
            <line
              x1={fromCenterX}
              y1={loopBottomY}
              x2={toLeftEdge}
              y2={loopBottomY}
              stroke={color}
              strokeWidth="2"
              strokeDasharray={strokeDasharray}
            />
            <line
              x1={toLeftEdge}
              y1={loopBottomY}
              x2={toLeftEdge}
              y2={toCenterY}
              stroke={color}
              strokeWidth="2"
              strokeDasharray={strokeDasharray}
              markerEnd={arrowMarker}
            />
            {isDecisionStep && decisionLabel && selectedLevel >= 2 && (
              <text
                x={(fromCenterX + toLeftEdge) / 2}
                y={loopBottomY + 18}
                fontSize="12"
                fill={labelColor}
                fontWeight="bold"
                textAnchor="middle"
                className="decision-label"
              >
                {decisionLabel}
              </text>
            )}
          </g>
        );
      }
    }

    if (isYesPath && toX > fromX) {
      const isCrossLane = from.position.y !== to.position.y;

      if (!isCrossLane || Math.abs(fromY - toY) < 20) {
        return (
          <g key={`${from.id}-${to.id}`}>
            <line
              x1={fromX}
              y1={fromY}
              x2={toX}
              y2={toY}
              stroke={color}
              strokeWidth="2"
              strokeDasharray={strokeDasharray}
              markerEnd={arrowMarker}
            />
            {isDecisionStep && decisionLabel && selectedLevel >= 2 && (
              <text
                x={(fromX + toX) / 2}
                y={fromY - 10}
                fontSize="12"
                fill={labelColor}
                fontWeight="bold"
                textAnchor="middle"
                className="decision-label"
              >
                {decisionLabel}
              </text>
            )}
          </g>
        );
      }

      const midX = fromX + (toX - fromX) * 0.7;

      return (
        <g key={`${from.id}-${to.id}`}>
          <line
            x1={fromX}
            y1={fromY}
            x2={midX}
            y2={fromY}
            stroke={color}
            strokeWidth="2"
            strokeDasharray={strokeDasharray}
          />
          <line
            x1={midX}
            y1={fromY}
            x2={midX}
            y2={toY}
            stroke={color}
            strokeWidth="2"
            strokeDasharray={strokeDasharray}
          />
          <line
            x1={midX}
            y1={toY}
            x2={toX}
            y2={toY}
            stroke={color}
            strokeWidth="2"
            strokeDasharray={strokeDasharray}
            markerEnd={arrowMarker}
          />

          {isDecisionStep && decisionLabel && selectedLevel >= 2 && (
            <text
              x={midX + 10}
              y={fromY - 10}
              fontSize="12"
              fill={labelColor}
              fontWeight="bold"
              className="decision-label"
            >
              {decisionLabel}
            </text>
          )}
        </g>
      );
    }

    if (isYesPath && toX <= fromX) {
      const isCrossLane = from.position.y !== to.position.y;
      const upOffset = 60;
      const topY = Math.min(fromY, toY) - upOffset;

      if (isCrossLane) {
        const verticalOffset = fromY < toY ? -50 : 50;
        const intermediateY = fromY + verticalOffset;
        const midX = fromX - Math.abs(fromX - toX) * 0.5;

        return (
          <g key={`${from.id}-${to.id}`}>
            <line
              x1={fromX}
              y1={fromY}
              x2={fromX}
              y2={intermediateY}
              stroke={color}
              strokeWidth="2"
              strokeDasharray={strokeDasharray}
            />
            <line
              x1={fromX}
              y1={intermediateY}
              x2={midX}
              y2={intermediateY}
              stroke={color}
              strokeWidth="2"
              strokeDasharray={strokeDasharray}
            />
            <line
              x1={midX}
              y1={intermediateY}
              x2={midX}
              y2={toY}
              stroke={color}
              strokeWidth="2"
              strokeDasharray={strokeDasharray}
            />
            <line
              x1={midX}
              y1={toY}
              x2={toX}
              y2={toY}
              stroke={color}
              strokeWidth="2"
              strokeDasharray={strokeDasharray}
              markerEnd={arrowMarker}
            />
            {isDecisionStep && decisionLabel && selectedLevel >= 2 && (
              <text
                x={fromX + 20}
                y={intermediateY - 8}
                fontSize="12"
                fill={labelColor}
                fontWeight="bold"
                className="decision-label"
              >
                {decisionLabel}
              </text>
            )}
          </g>
        );
      }

      return (
        <g key={`${from.id}-${to.id}`}>
          <line
            x1={fromX}
            y1={fromY}
            x2={fromX + 50}
            y2={fromY}
            stroke={color}
            strokeWidth="2"
            strokeDasharray={strokeDasharray}
          />
          <line
            x1={fromX + 50}
            y1={fromY}
            x2={fromX + 50}
            y2={topY}
            stroke={color}
            strokeWidth="2"
            strokeDasharray={strokeDasharray}
          />
          <line
            x1={fromX + 50}
            y1={topY}
            x2={toX}
            y2={topY}
            stroke={color}
            strokeWidth="2"
            strokeDasharray={strokeDasharray}
          />
          <line
            x1={toX}
            y1={topY}
            x2={toX}
            y2={toY}
            stroke={color}
            strokeWidth="2"
            strokeDasharray={strokeDasharray}
            markerEnd={arrowMarker}
          />
          {isDecisionStep && decisionLabel && selectedLevel >= 2 && (
            <text
              x={(fromX + toX) / 2}
              y={topY - 10}
              fontSize="12"
              fill={labelColor}
              fontWeight="bold"
              textAnchor="middle"
              className="decision-label"
            >
              {decisionLabel}
            </text>
          )}
        </g>
      );
    }

    return null;
  };

  const findOptimalPath = (
    fromX: number, fromY: number, toX: number, toY: number,
    isBackward: boolean = false,
    options?: { isDecisionToTrack?: boolean; trackType?: 'A' | 'B'; fromStep?: ExtendedProcessStep; toStep?: ExtendedProcessStep }
  ) => {
    const isSameLane = Math.abs(fromY - toY) < 20;
    const isSequential = !isBackward && (toX > fromX);
    const posIdx = options?.fromStep?.position?.x ? (options.fromStep.position.x % 4) : Math.floor(fromX / 100) % 4;

    if (options?.isDecisionToTrack && options.fromStep) {
      const fromBoxX = options.fromStep.position.x * (stepWidth + 50) + 30;
      const fromBoxY = calculateStepY(options.fromStep.position.y, options.fromStep);
      const diamondCenterX = fromBoxX + stepWidth / 2;
      const diamondRightX = fromBoxX + stepWidth;
      const diamondTopY = fromBoxY;
      const diamondBottomY = fromBoxY + stepHeight;

      if (options.trackType === 'A') {
        const exitX = diamondRightX - 10;
        const exitY = diamondTopY + 15;
        const bendX = exitX + 30;

        return {
          path: [
            { x: exitX, y: exitY },
            { x: bendX, y: exitY },
            { x: bendX, y: toY },
            { x: toX, y: toY }
          ],
          labelX: bendX + 15,
          labelY: exitY - 8
        };
      } else if (options.trackType === 'B') {
        const exitX = diamondCenterX;
        const exitY = diamondBottomY;
        const dropY = exitY + 40 + (posIdx * 25);

        return {
          path: [
            { x: exitX, y: exitY },
            { x: exitX, y: dropY },
            { x: toX - 20, y: dropY },
            { x: toX - 20, y: toY },
            { x: toX, y: toY }
          ],
          labelX: (exitX + toX - 20) / 2,
          labelY: dropY + 15
        };
      }
    }

    if (isSameLane && isSequential) {
      return {
        path: [{ x: fromX, y: fromY }, { x: toX, y: toY }],
        labelX: (fromX + toX) / 2,
        labelY: fromY - 15
      };
    }

    const baseOffset = 20 + (posIdx * 15);

    if (isBackward) {
      const midX = fromX + Math.abs(toX - fromX) * 0.5 + baseOffset;
      const baseBackOffset = 40 + (posIdx * 20);
      const offsetY = fromY > toY ? -baseBackOffset : baseBackOffset;
      return {
        path: [
          { x: fromX, y: fromY },
          { x: fromX + 20 + (posIdx * 10), y: fromY },
          { x: fromX + 20 + (posIdx * 10), y: fromY + offsetY },
          { x: toX - 20 - (posIdx * 10), y: fromY + offsetY },
          { x: toX - 20 - (posIdx * 10), y: toY },
          { x: toX, y: toY }
        ],
        labelX: midX,
        labelY: fromY + offsetY + (offsetY > 0 ? 10 : -20)
      };
    } else {
      const isCrossLane = Math.abs(fromY - toY) > 30;
      if (isCrossLane) {
        const baseTurnOffset = 20 + (posIdx * 10);
        const maxTurnOffset = Math.max(10, toX - fromX - 20);
        const turnOffset = Math.min(baseTurnOffset, maxTurnOffset);
        return {
          path: [
            { x: fromX, y: fromY },
            { x: fromX + turnOffset, y: fromY },
            { x: fromX + turnOffset, y: toY },
            { x: toX, y: toY }
          ],
          labelX: fromX + turnOffset + 10,
          labelY: (fromY + toY) / 2
        };
      }
      const verticalX = toX - 30 - (posIdx * 15);
      return {
        path: [
          { x: fromX, y: fromY },
          { x: verticalX, y: fromY },
          { x: verticalX, y: toY },
          { x: toX, y: toY }
        ],
        labelX: verticalX - 10,
        labelY: (fromY + toY) / 2 - 10
      };
    }
  };

  // The edge's meaning (label/kind/style) is supplied by buildEdges() as data.
  // renderConnector is now geometry-only: it routes the line between two steps
  // and prints edge.label with the style for edge.kind. No keyword/cross-lane
  // guessing, no yes/no inference. `connectionType` is derived from edge.kind
  // purely to pick the existing line-routing branch shape (top-exit vs bottom).
  const renderConnector = (from: ExtendedProcessStep, to: ExtendedProcessStep, edge?: MapEdge) => {

    const fromGlobalX = from.position.x;
    const toGlobalX = to.position.x;

    // buildEdges already validated this edge exists; always render it.
    if (!edge) return null;

    const fromBoxX = fromGlobalX * (stepWidth + 50) + 30;
    const fromBoxY = selectedLevel === 1 ? 50 : calculateStepY(from.position.y, from as ExtendedProcessStep);
    const toBoxX = toGlobalX * (stepWidth + 50) + 30;
    const toBoxY = selectedLevel === 1 ? 50 : calculateStepY(to.position.y, to as ExtendedProcessStep);

    const fromCenterY = fromBoxY + stepHeight / 2;
    const toCenterY = toBoxY + stepHeight / 2;

    let fromX, fromY, toX, toY;

    const isBackwardConnection = toGlobalX < fromGlobalX;
    const isForwardConnection = toGlobalX > fromGlobalX;
    const isSameLane = from.position.y === to.position.y;

    if (isBackwardConnection) {
      fromX = fromBoxX + stepWidth + 5;
      fromY = fromCenterY;
      toX = toBoxX + stepWidth + 5;
      toY = toCenterY;
    } else if (isForwardConnection && isSameLane) {
      fromX = fromBoxX + stepWidth + 5;
      fromY = fromCenterY;
      toX = toBoxX - 5;
      toY = toCenterY;
    } else if (isForwardConnection && !isSameLane) {
      fromX = fromBoxX + stepWidth + 5;
      fromY = fromCenterY;
      toX = toBoxX - 5;
      toY = toCenterY;
    } else {
      fromX = fromBoxX + stepWidth / 2;
      fromY = fromBoxY + stepHeight + 5;
      toX = toBoxX + stepWidth / 2;
      toY = toBoxY - 5;
    }

    // ── All semantics come from the edge (data), not from guessing ───────────
    const style = edgeStyle(edge.kind);
    const isDecisionStep = edge.kind === 'decision' || edge.kind === 'default';
    // Map edge.kind to the legacy routing-shape selector the geometry below uses:
    //  - a labeled decision/default branch routes like the old 'yes'/'no' paths
    //  - everything else routes as a plain sequence link
    const connectionType: 'yes' | 'no' | undefined =
      edge.kind === 'decision' ? 'yes' : edge.kind === 'default' ? 'no' : undefined;

    let color = style.color;
    let arrowMarker = style.arrow;
    let strokeDasharray = style.dash;
    let decisionLabel = edge.label || '';
    let labelColor = style.labelColor;

    // Check if target is a Track step
    const toIsTrackStep = (to as any).isTrackStep === true || to.id.includes('_TrackA') || to.id.includes('_TrackB');
    const toTrackType = (to as any).trackType || (to.id.includes('_TrackA') ? 'A' : (to.id.includes('_TrackB') ? 'B' : undefined));

    // Handle Yes path to Track A - exits from TOP of diamond, goes UP, then 90° turn RIGHT
    if (connectionType === 'yes' && toIsTrackStep && toTrackType === 'A') {
      const diamondCenterX = fromBoxX + stepWidth / 2;
      const diamondTopY = fromBoxY;
      const tgtLeftX = toBoxX - 5;
      const tgtCenterY = toBoxY + stepHeight / 2;

      const trackLabel = edge.label || (from as any).decisionPaths?.parallel_tracks?.track_A?.name || '';

      return (
        <g key={`${from.id}-${to.id}-yes-trackA`}>
          {/* Vertical line from diamond TOP going UP */}
          <line
            x1={diamondCenterX}
            y1={diamondTopY}
            x2={diamondCenterX}
            y2={tgtCenterY}
            stroke="#6B7280"
            strokeWidth="2"
          />
          {/* Horizontal line - 90° turn going RIGHT to Track A step */}
          <line
            x1={diamondCenterX}
            y1={tgtCenterY}
            x2={tgtLeftX}
            y2={tgtCenterY}
            stroke="#6B7280"
            strokeWidth="2"
            markerEnd="url(#decision-yes-arrowhead)"
          />
          {/* Label ABOVE the horizontal line */}
          {selectedLevel >= 2 && (
            <text
              x={(diamondCenterX + tgtLeftX) / 2}
              y={tgtCenterY - 8}
              fontSize="11"
              fill="#10B981"
              fontWeight="bold"
              textAnchor="middle"
              style={{
                paintOrder: 'stroke',
                stroke: 'white',
                strokeWidth: '3px'
              }}
            >
              {trackLabel}
            </text>
          )}
        </g>
      );
    }

    // Handle No path to Track B
    if (connectionType === 'no') {
        const targetOffset = to.position.y !== undefined ? (to.position.y % 3) * 15 : 0;
      const srcCenterX = fromBoxX + stepWidth / 2 + targetOffset;
      const srcBottomY = fromBoxY + stepHeight;

      if (from.id === to.id) {
        const loopRightX = fromBoxX + stepWidth + 40;
        const loopBottomY = srcBottomY + 50;
        const tgtLeftX = fromBoxX - 10;
        const tgtCenterY = fromBoxY + stepHeight / 2;

        return (
          <g key={`${from.id}-self-loop-no`}>
            <line x1={srcCenterX} y1={srcBottomY} x2={srcCenterX} y2={loopBottomY} stroke="#6B7280" strokeWidth="2" strokeDasharray="5,5" />
            <line x1={srcCenterX} y1={loopBottomY} x2={loopRightX} y2={loopBottomY} stroke="#6B7280" strokeWidth="2" strokeDasharray="5,5" />
            <line x1={loopRightX} y1={loopBottomY} x2={loopRightX} y2={fromBoxY - 30} stroke="#6B7280" strokeWidth="2" strokeDasharray="5,5" />
            <line x1={loopRightX} y1={fromBoxY - 30} x2={tgtLeftX} y2={fromBoxY - 30} stroke="#6B7280" strokeWidth="2" strokeDasharray="5,5" />
            <line x1={tgtLeftX} y1={fromBoxY - 30} x2={tgtLeftX} y2={tgtCenterY} stroke="#6B7280" strokeWidth="2" strokeDasharray="5,5" markerEnd="url(#decision-no-arrowhead)" />
            {selectedLevel >= 2 && (
              <text x={loopRightX + 10} y={(loopBottomY + fromBoxY - 30) / 2} fontSize="12" fill="#EF4444" fontWeight="bold" textAnchor="start">No</text>
            )}
          </g>
        );
      }

      const tgtLeftEdge = toBoxX - 5;
      const tgtCenterY = toBoxY + stepHeight / 2;
      const positionIndex = from.position.x % 4;
      const targetLaneOffset = (to.position.y || 0) * 20;
      const pathBottomY = srcBottomY + 40 + (positionIndex * 25) + targetLaneOffset;
      const verticalLineX = toBoxX - 35 - (positionIndex * 10) - targetLaneOffset;
      const noLabel = edge.label || (from as any).decisionPaths?.parallel_tracks?.track_B?.name || '';

      const targetIsForward = toGlobalX > fromGlobalX;
      const targetIsSameLane = from.position.y === to.position.y;
      const stepsApart = toGlobalX - fromGlobalX;

      // ALL No paths exit from BOTTOM of diamond
      const diamondBottomX = fromBoxX + stepWidth / 2;
      const diamondBottomY = fromBoxY + stepHeight;
      const bendY = diamondBottomY + 30 + (positionIndex * 15);

      if (targetIsForward && targetIsSameLane) {
        return (
          <g key={`${from.id}-${to.id}-no-path-same-lane`}>
            {/* Vertical line from diamond BOTTOM going down */}
            <line x1={diamondBottomX} y1={diamondBottomY} x2={diamondBottomX} y2={bendY} stroke="#6B7280" strokeWidth="2" strokeDasharray="5,5" />
            {/* Horizontal line going across to below target */}
            <line x1={diamondBottomX} y1={bendY} x2={tgtLeftEdge - 20} y2={bendY} stroke="#6B7280" strokeWidth="2" strokeDasharray="5,5" />
            {/* Vertical line going up to target */}
            <line x1={tgtLeftEdge - 20} y1={bendY} x2={tgtLeftEdge - 20} y2={tgtCenterY} stroke="#6B7280" strokeWidth="2" strokeDasharray="5,5" />
            {/* Final entry segment SOLID so the dash gaps can't hide the arrowhead at the very tip. */}
            <line x1={tgtLeftEdge - 20} y1={tgtCenterY} x2={tgtLeftEdge} y2={tgtCenterY} stroke="#6B7280" strokeWidth="2" markerEnd="url(#decision-no-arrowhead)" />
            {selectedLevel >= 2 && (
              <text x={(diamondBottomX + tgtLeftEdge) / 2} y={bendY - 8} fontSize="12" fill="#EF4444" fontWeight="bold" textAnchor="middle">{noLabel}</text>
            )}
          </g>
        );
      }

      if (targetIsForward && !targetIsSameLane) {
        const vertX = toBoxX - 40 - (positionIndex * 15);
        return (
          <g key={`${from.id}-${to.id}-no-path-cross`}>
            {/* Vertical line from diamond BOTTOM going down */}
            <line x1={diamondBottomX} y1={diamondBottomY} x2={diamondBottomX} y2={bendY} stroke="#6B7280" strokeWidth="2" strokeDasharray="5,5" />
            {/* Horizontal line going across */}
            <line x1={diamondBottomX} y1={bendY} x2={vertX} y2={bendY} stroke="#6B7280" strokeWidth="2" strokeDasharray="5,5" />
            {/* Vertical line going to target lane */}
            <line x1={vertX} y1={bendY} x2={vertX} y2={tgtCenterY} stroke="#6B7280" strokeWidth="2" strokeDasharray="5,5" />
            {/* Final entry segment SOLID so the dash gaps can't hide the arrowhead at the very tip. */}
            <line x1={vertX} y1={tgtCenterY} x2={tgtLeftEdge} y2={tgtCenterY} stroke="#6B7280" strokeWidth="2" markerEnd="url(#decision-no-arrowhead)" />
            {selectedLevel >= 2 && (
              <text x={(diamondBottomX + vertX) / 2} y={bendY - 8} fontSize="12" fill="#EF4444" fontWeight="bold" textAnchor="middle">{noLabel}</text>
            )}
          </g>
        );
      }

      return (
        <g key={`${from.id}-${to.id}-no-path`}>
          <line x1={diamondBottomX} y1={diamondBottomY} x2={diamondBottomX} y2={pathBottomY} stroke="#6B7280" strokeWidth="2" strokeDasharray="5,5" />
          <line x1={diamondBottomX} y1={pathBottomY} x2={verticalLineX} y2={pathBottomY} stroke="#6B7280" strokeWidth="2" strokeDasharray="5,5" />
          <line x1={verticalLineX} y1={pathBottomY} x2={verticalLineX} y2={tgtCenterY} stroke="#6B7280" strokeWidth="2" strokeDasharray="5,5" />
          {/* Final entry segment SOLID so the dash gaps can't hide the arrowhead at the very tip. */}
          <line x1={verticalLineX} y1={tgtCenterY} x2={tgtLeftEdge} y2={tgtCenterY} stroke="#6B7280" strokeWidth="2" markerEnd="url(#decision-no-arrowhead)" />
          {selectedLevel >= 2 && (
            <text x={(diamondBottomX + verticalLineX) / 2} y={pathBottomY - 8} fontSize="12" fill="#EF4444" fontWeight="bold" textAnchor="middle">{noLabel}</text>
          )}
        </g>
      );
    }


    const strokeWidth = '2';

    const isDecisionPath = connectionType === 'yes';
    const isRevisionLoop = to.name?.includes('Revision') || (to as any).step_number?.includes('A');

    if (isRevisionLoop) {
      const curvedPath = renderCurvedDecisionPath(
        from, to, fromX, fromY, toX, toY,
        color, strokeDasharray, arrowMarker, decisionLabel,
        connectionType, selectedLevel, isDecisionStep, labelColor
      );
      if (curvedPath) return curvedPath;
    }

    const isCrossLane = from.position.y !== to.position.y;

    const optimalRoute = findOptimalPath(fromX, fromY, toX, toY, isBackwardConnection, { fromStep: from, toStep: to });


    const pathElements: JSX.Element[] = [];
    const pathGroup = `path-${from.id}-${to.id}`;

    for (let i = 0; i < optimalRoute.path.length - 1; i++) {
      const isLastSegment = i === optimalRoute.path.length - 2;
      const currentPoint = optimalRoute.path[i];
      const nextPoint = optimalRoute.path[i + 1];

      pathElements.push(
        <line
          key={`${pathGroup}-segment-${i}`}
          x1={currentPoint.x}
          y1={currentPoint.y}
          x2={nextPoint.x}
          y2={nextPoint.y}
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={strokeDasharray}
          markerEnd={isLastSegment ? arrowMarker : 'none'}
          style={{
            // Higher z-index for backward connections to ensure arrow visibility
            zIndex: isBackwardConnection ? 1000 : 500
          }}
        />
      );
    }

    return (
      <g key={`${from.id}-${to.id}`} style={{ zIndex: isBackwardConnection ? 1000 : 500 }}>
        {pathElements}

        {/* SMART LABEL POSITIONING: Use optimal label position */}
        {isDecisionStep && decisionLabel && decisionLabel.length > 0 && selectedLevel >= 2 && (
          <text
            x={optimalRoute.labelX}
            y={optimalRoute.labelY}
            fontSize="12"
            fill={color}
            fontWeight="bold"
            textAnchor="middle"
            className="decision-label"
            style={{
              filter: 'drop-shadow(1px 1px 2px rgba(255,255,255,0.8))',
              zIndex: isBackwardConnection ? 1001 : 501
            }}
          >
            {decisionLabel}
          </text>
        )}
      </g>
    );
  };

  const { width: svgWidth, height: svgHeight } = React.useMemo(() => {
    if (!finalProcessSteps || finalProcessSteps.length === 0) {
      return { width: 1200, height: 600 };
    }

    const maxGlobalX = Math.max(...finalProcessSteps.map(s => {
      const globalPos = (s as any).globalPosition !== undefined ? (s as any).globalPosition : s.position.x;
      return globalPos;
    }));

    const minRequiredWidth = (maxGlobalX + 2) * (stepWidth + 50) + stepWidth + 500;
    const calculatedSvgWidth = Math.max(1800, minRequiredWidth);

    // L1 (Strategic): Single line height, L2-L4: Multi-lane height with dynamic heights
    let calculatedSvgHeight: number;
    if (selectedLevel === 1) {
      calculatedSvgHeight = stepHeight + 150;
    } else {
      let totalHeight = 0;
      for (let i = 0; i < roles.length; i++) {
        totalHeight += getLaneHeight(i);
      }
      calculatedSvgHeight = totalHeight + 200;
    }

    return {
      width: calculatedSvgWidth,
      height: calculatedSvgHeight
    };
  }, [finalProcessSteps, roles.length, stepWidth, laneHeight, selectedLevel]);

  const wrapText = (text: string, maxWidth: number, fontSize: number = 11, maxLines: number = 3) => {
    if (selectedLevel === 1) {
      fontSize = 9;
      maxLines = 2;
    } else if (selectedLevel === 2) {
      fontSize = 10;
      maxLines = 3;
    } else if (selectedLevel === 3) {
      fontSize = 11;
      maxLines = 4;
    } else if (selectedLevel === 4) {
      fontSize = 10;
      maxLines = 4;
    } else {
      fontSize = 11;
      maxLines = 3;
    }
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';

    const charWidth = fontSize * 0.46;
    const maxCharsPerLine = Math.floor(maxWidth / charWidth);

    for (const word of words) {
      if (lines.length >= maxLines) break;

      const testLine = currentLine + (currentLine ? ' ' : '') + word;
      if (testLine.length <= maxCharsPerLine) {
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
          if (lines.length >= maxLines) break;
          currentLine = word;
        } else {
          const truncatedWord = word.length > maxCharsPerLine ? word.substring(0, maxCharsPerLine - 3) + '...' : word;
          lines.push(truncatedWord);
          currentLine = '';
        }
      }
    }

    if (currentLine && lines.length < maxLines) {
      lines.push(currentLine);
    }

    if (lines.length === maxLines && words.join(' ').length > lines.join(' ').length) {
      const lastLine = lines[lines.length - 1];
      if (lastLine.length > 3) {
        lines[lines.length - 1] = lastLine.substring(0, lastLine.length - 3) + '...';
      }
    }

    return lines;
  };

  const getProcessColorScheme = (step: ExtendedProcessStep) => {
    const s = step as any;
    const frictionData = frictionDataMap?.get(String(s.step_number))
      || frictionDataMap?.get(String(s.id))
      || frictionDataMap?.get(s.name || '')
      || frictionDataMap?.get(`Step ${s.step_number}`)
      || frictionDataMap?.get(s.step_id || '');

    if (showFrictionHeatmap && frictionData?.friction_color) {
      const frictionColor = frictionData.friction_color;
      const darkenColor = (hex: string, percent: number): string => {
        const num = parseInt(hex.replace('#', ''), 16);
        const amt = Math.round(2.55 * percent);
        const R = Math.max(0, (num >> 16) - amt);
        const G = Math.max(0, ((num >> 8) & 0x00FF) - amt);
        const B = Math.max(0, (num & 0x0000FF) - amt);
        return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
      };

      return {
        fillColor: frictionColor,
        strokeColor: darkenColor(frictionColor, 20),
        strokeWidth: '3'
      };
    }

    const getMonochromeColorScheme = (actionType: string | undefined, isTrackStep: boolean): { fillColor: string; strokeColor: string } => {
      const fillColor = '#FFFFFF';
      const type = actionType?.toLowerCase() || 'process';

      switch (type) {
        case 'decision':
        case 'approval':
        case 'review':
        case 'validation':
          return { fillColor, strokeColor: '#1F2937' };

        case 'start':
        case 'end':
          return { fillColor, strokeColor: '#374151' };

        default:
          if (isTrackStep) {
            return { fillColor, strokeColor: '#9CA3AF' };
          }
          return { fillColor, strokeColor: '#4B5563' };
      }
    };

    const isTrackStep = (step as any).isTrackStep || false;
    const actionType = (step as any).action_type;

    const { fillColor, strokeColor } = getMonochromeColorScheme(actionType, isTrackStep);

    const getStrokeWidth = (actionType: string): string => {
      if (!actionType) return '2';
      const type = actionType.toLowerCase();
      return ['decision', 'approval', 'review', 'validation'].includes(type) ? '3' : '2';
    };

    return {
      fillColor,
      strokeColor,
      strokeWidth: getStrokeWidth(actionType || '')
    };
  };


  const calculateStepY = (laneIndex: number, step?: ExtendedProcessStep) => {
    const baseLaneIndex = Math.floor(laneIndex);
    const fractionalOffset = laneIndex - baseLaneIndex;

    const cumulativeHeight = getTotalHeightUpToLane(baseLaneIndex);
    const currentLaneHeight = baseLaneIndex === roles.length - 1
      ? getLaneHeight(baseLaneIndex) + 200
      : getLaneHeight(baseLaneIndex);

    const isTrackStep = (step as any)?.isTrackStep;
    const trackType = (step as any)?.trackType;

    let baseY: number;

    if (isTrackStep && trackType) {
      const trackSpacing = stepHeight + 20;
      const totalTrackHeight = trackSpacing * 2;
      const laneCenter = cumulativeHeight + currentLaneHeight / 2;

      if (trackType === 'A') {
        baseY = laneCenter - trackSpacing / 2 - stepHeight / 2;
      } else {
        baseY = laneCenter + trackSpacing / 2 - stepHeight / 2;
      }
    } else {
      baseY = cumulativeHeight + (currentLaneHeight - stepHeight) / 2;

      if (fractionalOffset !== 0) {
        baseY = baseY + (fractionalOffset * currentLaneHeight * 0.3);
      }
    }

    return baseY;
  };

  const renderStep = (step: ExtendedProcessStep) => {
    const x = step.position.x * (stepWidth + 50) + 30;
    const y = selectedLevel === 1 ? 50 : calculateStepY(step.position.y, step);
    const centerX = x + stepWidth / 2;
    const centerY = y + stepHeight / 2;

    const { fillColor, strokeColor, strokeWidth } = getProcessColorScheme(step);

    const maxTextWidth = stepWidth * 0.85;
    let fontSize = 11;
    let maxLines = 3;

    if (selectedLevel === 1) {
      fontSize = 9;
      maxLines = 2;
    } else if (selectedLevel === 2) {
      fontSize = 10;
      maxLines = 3;
    } else if (selectedLevel === 3) {
      fontSize = 11;
      maxLines = 4;
    } else if (selectedLevel === 4) {
      fontSize = 10;
      maxLines = 4;
    }

    const shortTitle = extractStepTitle(step.name);
    const lines = wrapText(shortTitle, maxTextWidth, fontSize, maxLines);
    const lineHeight = fontSize + 2;
    const totalTextHeight = lines.length * lineHeight;
    const startY = centerY - totalTextHeight / 2 + fontSize / 2;

    const shapeType = determineShapeType(step);

    const handleStepClick = () => {
      // TODO: Implement audit info for Level 1, 2, and 4 later
      // Currently only Level 3 shows audit information
      if (selectedLevel === 3 && currentMap?.id) {
        const activityId = step.id.replace(/^L\d+_/, '');

        const mapStep = currentMap.map_data?.elements?.processes?.find((p: { id: string; }) => p.id === activityId);
        const originalText = mapStep?.full_text || step.name;
        handleStepAuditClick(step.id, step.name, originalText);
      }
    };

    const hasAuditData = auditSummary?.step_audit_summary[step.id]?.has_audit_data;
    const exceptionInfo = stepExceptionData.get(step.id);

    return (
      <g key={step.id}>
        {shapeType === 'diamond' ? (
          <>
            <polygon
              points={`${centerX},${y} ${x + stepWidth},${centerY} ${centerX},${y + stepHeight} ${x},${centerY}`}
              fill={fillColor}
              stroke={strokeColor}
              strokeWidth={strokeWidth}
              className={`decision-step ${showAuditInfo ? 'cursor-pointer hover:opacity-80' : ''} transition-opacity`}
              onClick={handleStepClick}
            />
            <text
              x={centerX}
              y={centerY}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={fontSize + 10}
              fontWeight="900"
              fill="#1F2937"
              stroke="#1F2937"
              strokeWidth="1"
              className="gateway-symbol"
              style={{
                paintOrder: 'stroke fill',
                letterSpacing: '0.5px'
              }}
            >
              {getGatewayTypeSymbol(step)}
            </text>
          </>
        ) : shapeType === 'oval' ? (
          <ellipse
            cx={centerX}
            cy={centerY}
            rx={stepWidth / 2 - 5}
            ry={stepHeight / 2 - 5}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            className={`start-end-step ${showAuditInfo ? 'cursor-pointer hover:opacity-80' : ''} transition-opacity`}
            onClick={handleStepClick}
          />
        ) : (
          <rect
            x={x}
            y={y}
            width={stepWidth}
            height={stepHeight}
            rx="6"
            ry="6"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            className={`process-step ${showAuditInfo ? 'cursor-pointer hover:opacity-80' : ''} transition-opacity`}
            onClick={handleStepClick}
          />
        )}

        {showAuditInfo && hasAuditData && (
          <circle
            cx={x + stepWidth - 8}
            cy={y + 8}
            r="4"
            fill="#3B82F6"
            stroke="#ffffff"
            strokeWidth="1"
            className="cursor-pointer"
            onClick={handleStepClick}
          />
        )}

        {lines.map((line, lineIndex) => (
          <React.Fragment key={lineIndex}>
            <text
              x={centerX}
              y={startY + lineIndex * lineHeight}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={fontSize}
              fontWeight="500"
              fill="#1F2937"
              className={`process-text ${showAuditInfo ? 'cursor-pointer' : ''}`}
              onClick={handleStepClick}
            >
              {line}
            </text>
          </React.Fragment>
        ))}

        <circle
          cx={x + stepWidth - 12}
          cy={y + 12}
          r="8"
          fill="#4B5563"
          stroke="#ffffff"
          strokeWidth="1"
          className={showAuditInfo ? 'cursor-pointer' : ''}
          onClick={handleStepClick}
        />
        <text
          x={x + stepWidth - 12}
          y={y + 12}
          textAnchor="middle"
          dominantBaseline="middle"
          className={`text-xs font-bold fill-white ${showAuditInfo ? 'cursor-pointer' : ''}`}
          style={{ fontSize: '8px' }}
          onClick={handleStepClick}
        >
          {step.position.x + 1}
        </text>

        {showFrictionHeatmap && (() => {
          const s = step as any;
          const frictionData = frictionDataMap?.get(String(s.step_number))
            || frictionDataMap?.get(String(s.id))
            || frictionDataMap?.get(s.name || '')
            || frictionDataMap?.get(`Step ${s.step_number}`)
            || frictionDataMap?.get(s.step_id || '');
          if (frictionData?.has_data && frictionData.friction_score !== null) {
            return (
              <>
                <rect
                  x={x + 4}
                  y={y + 4}
                  width="24"
                  height="16"
                  rx="4"
                  fill={frictionData.friction_color || '#666'}
                  stroke="#ffffff"
                  strokeWidth="1"
                />
                <text
                  x={x + 16}
                  y={y + 12}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="text-xs font-bold fill-white"
                  style={{ fontSize: '9px' }}
                >
                  {Math.round(frictionData.friction_score)}
                </text>
              </>
            );
          }
          return null;
        })()}

        {exceptionInfo?.hasExceptions && (
          <>
            <circle
              cx={x + stepWidth - 28}
              cy={y + 12}
              r="8"
              fill={exceptionInfo?.hasHighRisk ? '#DC2626' : '#F59E0B'}
              stroke="#ffffff"
              strokeWidth="2"
              className="exception-indicator cursor-pointer hover:opacity-80 transition-opacity"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedStepException({
                  stepName: step.name,
                  stepId: step.id,
                  exceptionData: exceptionInfo,
                  processMapId: currentMap?.id
                });
                setExceptionPopupOpen(true);
              }}
            />
            <text
              x={x + stepWidth - 28}
              y={y + 12}
              textAnchor="middle"
              dominantBaseline="middle"
              className="text-xs font-bold fill-white pointer-events-none"
              style={{ fontSize: '8px' }}
            >
              !
            </text>
          </>
        )}

        {/* Show green checkmark when analysis completed but no exceptions found */}
        {!exceptionInfo?.hasExceptions &&
         currentMap?.audit_trail?.exception_analysis_status === 'completed' &&
         currentMap?.audit_trail?.exception_analysis?.detected_exceptions?.length === 0 && (
          <>
            <circle
              cx={x + stepWidth - 28}
              cy={y + 12}
              r="8"
              fill="#10B981"
              stroke="#ffffff"
              strokeWidth="2"
              className="no-exception-indicator cursor-pointer hover:opacity-80 transition-opacity"
            />
            <text
              x={x + stepWidth - 28}
              y={y + 12}
              textAnchor="middle"
              dominantBaseline="middle"
              className="text-xs font-bold fill-white pointer-events-none"
              style={{ fontSize: '8px' }}
            >
              ✓
            </text>
          </>
        )}

        {/* ✅ PARALLEL TRACKS INDICATORS */}
        {(step as any).isTrackStep && (
          <>
            {/* Track Badge */}
            <rect
              x={x + stepWidth - 25}
              y={y - 8}
              width="20"
              height="14"
              rx="7"
              fill={(step as any).trackType === 'A' ? '#4CAF50' : '#FF9800'}
              stroke="#ffffff"
              strokeWidth="1"
              className="track-indicator"
            />
            <text
              x={x + stepWidth - 15}
              y={y - 1}
              textAnchor="middle"
              dominantBaseline="middle"
              className="text-xs font-bold fill-white pointer-events-none"
              style={{ fontSize: '9px' }}
            >
              {(step as any).trackType}
            </text>

            {/* Track Name Label (on hover or for Level 3+) */}
            {selectedLevel >= 3 && (step as any).trackName && (
              <text
                x={centerX}
                y={y + stepHeight + 15}
                textAnchor="middle"
                className="text-xs fill-gray-600"
                style={{ fontSize: '8px' }}
              >
                {(step as any).trackName}
              </text>
            )}
          </>
        )}

        <title>{renderStepTooltip(step, showAuditInfo)}</title>
      </g>
    );
  };

  const determineShapeType = (step: ExtendedProcessStep): 'rectangle' | 'diamond' | 'oval' => {
    const bpmnType = (step as any).bpmn_type;
    const actionType = (step.action_type || '').toLowerCase();
    const stepName = (step.name || '').toLowerCase();

    if (bpmnType === 'event') {
      return 'oval';
    }

    if (bpmnType === 'gateway') {
      return 'diamond';
    }

    if (stepName.startsWith('end') || stepName.startsWith('start')) {
      return 'oval';
    }

    switch (actionType) {
      case 'decision':
      case 'approval':
      case 'review':
      case 'validation':
      case 'split':
      case 'merge':
      case 'gateway':
        return 'diamond';
      case 'start':
      case 'end':
        return 'oval';
      default:
        return 'rectangle';
    }
  };

  const getGatewayTypeSymbol = (step: ExtendedProcessStep): string => {
    const gatewayType = (step as any).gateway_type as GatewayType;
    return getGatewaySymbol(gatewayType);
  };

  const getStepBpmnLabel = (step: ExtendedProcessStep): string => {
    const bpmnType = (step as any).bpmn_type as BpmnType;
    const taskType = (step as any).task_type as TaskType;
    const gatewayType = (step as any).gateway_type as GatewayType;
    const eventType = (step as any).event_type as EventType;

    if (bpmnType === 'event' && eventType) {
      return EVENT_TYPE_LABELS[eventType] || eventType;
    }
    if (bpmnType === 'gateway' && gatewayType) {
      return GATEWAY_TYPE_LABELS[gatewayType] || gatewayType;
    }
    if (bpmnType === 'task' && taskType) {
      return TASK_TYPE_LABELS[taskType] || taskType;
    }
    return '';
  };

  useEffect(() => {
    if (onExportReady) {
      onExportReady({
        downloadExcel: () => downloadAsExcel(roles, finalProcessSteps as any[]),
        downloadPNG: downloadAsPNG,
        downloadPDF: downloadAsPDF,
        downloadBPMN: () => {
          try {
            const processName = currentMap?.name || 'Process_Map';
            const processId = currentMap?.id ? `Process_${currentMap.id}` : 'Process_1';
            downloadBpmnFile(finalProcessSteps as ExtendedProcessStep[], processName, processId);
            toast.success('BPMN file exported successfully!');
          } catch (error) {
            console.error('BPMN export error:', error);
            toast.error('Failed to export BPMN file');
          }
        },
        isDownloading,
      });
    }
  }, [onExportReady, isDownloading, roles, finalProcessSteps, currentMap]);

  if ((!swimlanes || swimlanes.length === 0) && (!processes || processes.length === 0)) {
    return null;
  }

  if (isEditMode && onProcessReorder) {
    const editableSteps = Array.isArray(processes) ? processes : [];
    const mapStakeholders = stakeholderNamesFromMap(currentMap?.map_data);
    const assigneeOptions = Array.from(new Set([...roles, ...extraStakeholders, ...mapStakeholders].filter(Boolean)));
    const proposed = overlayFindings(findings);
    const named = stakeholderFindings(findings);
    const toc = [...proposed, ...findings.filter((f) => f.status === 'proposed' || f.status === 'kept_flagged')];
    const seenToc = new Set<string>();
    const tocUnique = toc.filter((f) => {
      if (seenToc.has(f.id)) return false;
      seenToc.add(f.id);
      return true;
    });
    return (
      <div className="bg-white rounded-lg border p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Edit Mode - Reorder Steps
          </h3>
          <p className="text-sm text-gray-600">
            Drag steps to reorder them, or use the up/down arrows. Click the <span className="inline-flex items-center text-blue-600 font-medium">✏️ Edit</span> button to modify step name, description, stakeholder, or mark a decision (YES/NO). Dashed rows are verifier proposals — Approve, Adjust, or Reject. Nothing is written to the map until Approve or Adjust+save.
          </p>
        </div>
        {tocUnique.length > 0 && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-amber-800 mb-2">
              {tocUnique.length} proposed correction{tocUnique.length === 1 ? '' : 's'}
            </div>
            <ul className="space-y-1">
              {tocUnique.map((f) => (
                <li key={f.id}>
                  <button
                    type="button"
                    className="text-left text-xs text-amber-900 hover:underline"
                    onClick={() => {
                      const el = document.querySelector(`[data-finding-id="${f.id.replace(/"/g, '')}"]`);
                      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }}
                  >
                    {findingReason(f)}
                  </button>
                </li>
              ))}
            </ul>
            {named.length > 0 && (
              <p className="mt-2 text-[11px] text-amber-800">Named participants are added as stakeholders only — they do not get a new swimlane.</p>
            )}
          </div>
        )}
        <ReorderableProcessList
          key={editableSteps.map(p => p.id).join('-')}
          processes={editableSteps}
          onReorder={onProcessReorder}
          availableStakeholders={assigneeOptions}
          findings={findings}
          sourceHasDiamonds={sourceHasDiamonds}
          onFindingAction={onFindingAction}
          onDecisionEdit={onDecisionEdit}
          onStepEdit={(index, updatedStep) => {
            const newProcesses = [...editableSteps];
            newProcesses[index] = updatedStep;
            onProcessReorder(newProcesses);
          }}
          onStepDelete={(index, step) => {
            if (onStepDelete) {
              onStepDelete(step.id);
            }
          }}
          onStepAdd={(insertIndex, newStep) => {
            const newProcesses = [...editableSteps];
            newProcesses.splice(insertIndex, 0, newStep);
            onProcessReorder(newProcesses);
          }}
          onPendingDeleteChange={onPendingDeleteChange}
        />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border overflow-hidden">
      <div className="px-4 py-2 border-b bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 flex-wrap gap-y-1">
            <h4 className="font-semibold text-sm text-gray-900">
              {getLevelName(selectedLevel)}
            </h4>
            <span className="text-xs text-gray-500">{finalProcessSteps.length} steps</span>

            {/*
              The "No exceptions" chip was removed. It rendered a green pill whose
              provenance was not stated anywhere on screen, and with exception
              analysis now deferred to export it would report a zero it had not
              actually checked. Only the positive finding (below) is shown, because
              only that is actionable.
            */}

            {currentMap?.audit_trail?.exception_analysis_status === 'completed' &&
             currentMap?.audit_trail?.exception_analysis?.detected_exceptions?.length > 0 && (
              <span
                className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full text-xs font-medium cursor-help"
                title={`${currentMap.audit_trail.exception_analysis.detected_exceptions.length} exception scenarios identified. Click orange indicators on steps for details.`}
              >
                <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                {currentMap.audit_trail.exception_analysis.detected_exceptions.length} exceptions
              </span>
            )}

            {currentMap?.audit_trail?.exception_analysis_status === 'pending' && (
              <span className="inline-flex items-center gap-1 text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full text-xs font-medium">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
                Analyzing...
              </span>
            )}

            {currentMap?.audit_trail?.exception_analysis_status === 'failed' && (
              <span className="inline-flex items-center gap-1 text-red-700 bg-red-50 px-2 py-0.5 rounded-full text-xs font-medium">
                <span className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                Analysis failed
              </span>
            )}
          </div>

          {/*
            The "Audit Info Ready" chip was removed along with the in-UI audit panel.
            It reported the state of a background job whose output is no longer
            rendered on this screen — the audit trail is now built on demand and
            downloaded from Export -> Audit Trail.
          */}
        </div>
      </div>

      {finalProcessSteps.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border-2 border-dashed border-blue-200 p-12 min-h-[600px]">
          <div className="text-center max-w-lg">
            <div className="mb-8">
              <div className="text-6xl animate-bounce">🤖</div>
              <div className="flex justify-center space-x-1 mt-4">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>

            <div className="space-y-4">

              <div className="bg-white rounded-lg p-4 border border-blue-200 shadow-sm">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{finalProcessSteps.length}</div>
                    <div className="text-xs text-gray-500">Process Steps</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-purple-600">{roles.length}</div>
                    <div className="text-xs text-gray-500">Stakeholder Roles</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">{stakeholderCounts?.exactStakeholders || 0}</div>
                    <div className="text-xs text-gray-500">Mapped Stakeholders</div>
                  </div>
                </div>
              </div>


            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col">
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 mt-2 mb-2 mx-4 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-amber-600 text-lg">&#9432;</span>
              <span className="text-sm text-amber-800">
                AI-generated process maps may occasionally have visual inconsistencies. If the layout doesn't look correct, please regenerate the process map for better results.
              </span>
            </div>
          </div>
          <TopScrollbar scrollableRef={swimlanesRef} />
          <div
            className="flex overflow-auto custom-scrollable"
            ref={swimlanesRef}
            {...canvasProps}
            style={{
              ...canvasProps.style,
              maxHeight: '80vh',
              minHeight: '400px',
            }}
          >
            {selectedLevel !== 1 && (
              <div className="bg-gray-100 border-r flex-shrink-0 sticky left-0 z-10 shadow-md" style={{ width: `${160 * (zoomLevel / 100)}px` }}>
                {roles.map((role, index) => {
                  const isLastLane = index === roles.length - 1;
                  const dynamicLaneHeight = getLaneHeight(index);
                  const finalHeight = isLastLane ? dynamicLaneHeight + 200 : dynamicLaneHeight;
                  const scaledHeight = finalHeight * (zoomLevel / 100);

                  return (
                    <div
                      key={role}
                      className="flex items-center justify-center font-medium text-gray-700 border-b bg-gradient-to-r from-gray-100 to-gray-50 px-2"
                      style={{
                        height: `${scaledHeight}px`,
                        fontSize: `${14 * (zoomLevel / 100)}px`
                      }}
                    >
                      <span className="text-center leading-tight">{role}</span>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex-1">

            <div>
              <div
                className="flex-shrink-0"
                ref={containerRef}
                style={{
                  width: `${svgWidth * (zoomLevel / 100)}px`,
                  minWidth: `${svgWidth * (zoomLevel / 100)}px`,
                  height: `${svgHeight * (zoomLevel / 100)}px`,
                  transform: `scale(${zoomLevel / 100})`,
                  transformOrigin: 'top left',
                  transition: 'transform 0.2s ease-out'
                }}
              >
                <svg
                  width={svgWidth}
                  height={svgHeight}
                  viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                  className="bg-white block"
                  preserveAspectRatio="xMinYMin meet"
                >
                  <defs>
                    {/* ENHANCED ARROW MARKERS with better visibility and z-index management */}
                    <marker
                      id="arrowhead"
                      markerWidth="12"
                      markerHeight="10"
                      refX="11"
                      refY="5"
                      orient="auto"
                      markerUnits="strokeWidth"
                      style={{ zIndex: 1002 }}
                    >
                      <polygon
                        points="0 1, 11 5, 0 9"
                        fill="#3b82f6"
                        stroke="#ffffff"
                        strokeWidth="0.5"
                        style={{ filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.3))' }}
                      />
                    </marker>
                    <marker
                      id="decision-yes-arrowhead"
                      markerWidth="12"
                      markerHeight="10"
                      refX="11"
                      refY="5"
                      orient="auto"
                      markerUnits="strokeWidth"
                      style={{ zIndex: 1002 }}
                    >
                      <polygon
                        points="0 1, 11 5, 0 9"
                        fill="#4CAF50"
                        stroke="#ffffff"
                        strokeWidth="0.5"
                        style={{ filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.3))' }}
                      />
                    </marker>
                    <marker
                      id="decision-no-arrowhead"
                      markerWidth="12"
                      markerHeight="10"
                      refX="11"
                      refY="5"
                      orient="auto"
                      markerUnits="strokeWidth"
                      style={{ zIndex: 1002 }}
                    >
                      <polygon
                        points="0 1, 11 5, 0 9"
                        fill="#F44336"
                        stroke="#ffffff"
                        strokeWidth="0.5"
                        style={{ filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.3))' }}
                      />
                    </marker>
                    <marker
                      id="track-b-arrowhead"
                      markerWidth="10"
                      markerHeight="8"
                      refX="9"
                      refY="4"
                      orient="auto"
                      markerUnits="strokeWidth"
                    >
                      <polygon points="0 0, 10 4, 0 8" fill="#FF9800" />
                    </marker>
                    <marker
                      id="sequential-arrowhead"
                      markerWidth="10"
                      markerHeight="8"
                      refX="9"
                      refY="4"
                      orient="auto"
                      markerUnits="strokeWidth"
                    >
                      <polygon points="0 0, 10 4, 0 8" fill="#333333" />
                    </marker>
                  </defs>

                  {/* Swimlane dividers - Hide for Level 1 (no swimlanes) */}
                  {selectedLevel !== 1 && roles.map((_, index) => {
                    const cumulativeHeight = getTotalHeightUpToLane(index + 1);

                    return (
                      <line
                        key={`divider-${index}`}
                        x1="0"
                        y1={cumulativeHeight}
                        x2={svgWidth}
                        y2={cumulativeHeight}
                        stroke="#e5e7eb"
                        strokeWidth="1"
                      />
                    );
                  })}

                  {/* Render valid connections */}
                  {renderValidConnections()}

                  {/* Render process steps */}
                  {finalProcessSteps.map(renderStep)}
                </svg>
              </div>
            </div>
            </div>
          </div>
        </div>
      )}

      {auditPopupOpen && selectedStepForAudit && (
        <StepAuditPopup
          isOpen={auditPopupOpen}
          onClose={() => setAuditPopupOpen(false)}
          stepId={selectedStepForAudit.stepId}
          stepName={selectedStepForAudit.full_text}
          full_text={selectedStepForAudit.full_text}
          processMapId={selectedStepForAudit.processMapId}
          projectId={currentProject?.id || 0}
          processId={selectedStepForAudit.processId}
          stepDbId={selectedStepForAudit.stepDbId}
          validationStatus={selectedStepForAudit.validationStatus}
          isLocked={selectedStepForAudit.isLocked}
          onStatusUpdate={() => {
            // Refresh the step data after status update
            if (selectedStepForAudit.stepId) {
              handleStepAuditClick(
                selectedStepForAudit.stepId,
                selectedStepForAudit.stepName,
                selectedStepForAudit.full_text
              );
            }
          }}
        />
      )}

      {exceptionPopupOpen && selectedStepException && (
        <ExceptionAnalysisPopup
          isOpen={exceptionPopupOpen}
          onClose={() => setExceptionPopupOpen(false)}
          stepName={selectedStepException.stepName}
          stepId={selectedStepException.stepId}
          exceptionData={selectedStepException.exceptionData}
          processMapId={selectedStepException.processMapId}
        />
      )}

    </div>
  );

};

export default SwimlanesVisualization;